// react and js
import React, { forwardRef, useImperativeHandle, useState, useRef, useEffect } from "react";
import { useFormik, FormikProvider, FastField } from "formik";
import PropTypes from "prop-types";
import { enCA } from "date-fns/locale";

// mui
import {
  Stack,
  Box,
  IconButton,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  FormControl,
  Select,
  FormControlLabel,
  Radio,
  CardMedia,
  Divider,
  Collapse,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

// components
import ModalDialog from "../../../components/ModalDialog";
import RequestMessage from "../../../components/RequestMessage";
import Iconify from "../../../components/Iconify";
import SmartParameter from "../../../components/SmartParameter";
import { SmartTextInput, SmartNumberInput } from "../../../components/SmartInputs";

// theme
import {
  B1Tiny,
  B3Tiny,
  ModalActionsLoadingButton,
  ModalContentOutlinedInput,
  ModalContentMenuItem,
  ModalContentSwitch,
  ModalContentRadioGroup,
  ModalTimePicker,
  ModalTimePickerSlotProps,
} from "../../../theme/styled";

// ----------------------------------------------------------------------
// Help Images Configuration
// ----------------------------------------------------------------------
const VALUE_CONDITION_HELP_SECTIONS = {
  0: {
    image: "/illustrations/converted/indicators/value_off.png",
    description: (
      <>
        If the value condition is set to <b>Off</b>, this part of the indicator signal block <b>always evaluates as a positive signal</b>.
      </>
    ),
  },
  1: {
    image: "/illustrations/converted/indicators/value_smaller_limit_1.png",
    description: (
      <>
        The indicator value is evaluated as a positive signal if <b>smaller than Value limit 1</b>.
      </>
    ),
  },
  2: {
    image: "/illustrations/converted/indicators/value_greater_limit_1.png",
    description: (
      <>
        The indicator value is evaluated as a positive signal if <b>greater than Value limit 1</b>.
      </>
    ),
  },
  3: {
    image: "/illustrations/converted/indicators/value_equals_limit_1.png",
    description: (
      <>
        The indicator value is evaluated as a positive signal if it is <b>precisely the same as Value limit 1</b>.
      </>
    ),
  },
  4: {
    image: "/illustrations/converted/indicators/value_between_limit_1_and_limit_2.png",
    description: (
      <>
        The indicator value is evaluated as a positive signal if it is <b>between Value limit 1 and Value limit 2</b>.
      </>
    ),
  },
  5: {
    image: "/illustrations/converted/indicators/value_outside_limit_1_and_limit_2.png",
    description: (
      <>
        The indicator value is evaluated as a positive signal if it is <b>outside of Value limit 1 and Value limit 2</b>.
      </>
    ),
  },
  6: {
    image: "/illustrations/converted/indicators/value_cross_limit_1.png",
    description: (
      <>
        The indicator value is evaluated as a positive signal if it <b>crosses Value limit 1</b>.
      </>
    ),
  },
  7: {
    image: "/illustrations/converted/indicators/value_limit_1_crossing.png",
    description: (
      <>
        The indicator value is evaluated as a positive signal if it <b>is crossed by Value limit 1</b>.
      </>
    ),
  },
  8: {
    image: "/illustrations/converted/indicators/value_cross_any.png",
    description: (
      <>
        The indicator value is evaluated as a positive signal if <b>any crossing of the value line and Value limit 1 occurs</b>.
      </>
    ),
  },
};

const GRADIENT_CONDITION_HELP_SECTIONS = {
  0: {
    image: "/illustrations/converted/indicators/gradient_off.png",
    description: (
      <>
        If the value condition is set to <b>Off</b>, this part of the indicator signal block <b>always evaluates as a positive signal</b>.
      </>
    ),
  },
  1: {
    image: "/illustrations/converted/indicators/gradient_smaller_limit_1.png",
    description: (
      <>
        The indicator gradient is evaluated as a positive signal if <b>smaller than Gradient limit 1</b>.
      </>
    ),
  },
  2: {
    image: "/illustrations/converted/indicators/gradient_greater_limit_1.png",
    description: (
      <>
        The indicator gradient is evaluated as a positive signal if <b>greater than Gradient limit 1</b>.
      </>
    ),
  },
  3: {
    image: "/illustrations/converted/indicators/gradient_equals_limit_1.png",
    description: (
      <>
        The indicator gradient is evaluated as a positive signal if it is <b>precisely the same as Gradient limit 1</b>.
      </>
    ),
  },
  4: {
    image: "/illustrations/converted/indicators/gradient_between_limit_1_and_limit_2.png",
    description: (
      <>
        The indicator gradient is evaluated as a positive signal if it is <b>between Gradient limit 1 and Gradient limit 2</b>.
      </>
    ),
  },
  5: {
    image: "/illustrations/converted/indicators/gradient_outside_limit_1_and_limit_2.png",
    description: (
      <>
        The indicator gradient is evaluated as a positive signal if it is <b>outside of Gradient limit 1 and Gradient limit 2</b>.
      </>
    ),
  },
  6: {
    image: "/illustrations/converted/indicators/gradient_cross_limit_1.png",
    description: (
      <>
        The indicator gradient is evaluated as a positive signal if it <b>crosses Gradient limit 1</b>.
      </>
    ),
  },
  7: {
    image: "/illustrations/converted/indicators/gradient_limit_1_crossing.png",
    description: (
      <>
        The indicator gradient is evaluated as a positive signal if it <b>is crossed by Gradient limit 1</b>.
      </>
    ),
  },
  8: {
    image: "/illustrations/converted/indicators/gradient_cross_any.png",
    description: (
      <>
        The indicator gradient is evaluated as a positive signal if <b>any crossing of the Gradient line and Gradient limit 1 occurs</b>.
      </>
    ),
  },
};

// ----------------------------------------------------------------------
// Section
// ----------------------------------------------------------------------
const getNextAvailableDrawingSlot = (indicators) => {
  if (!indicators) {
    return "1";
  }

  const usedSlots = new Set();

  // Handle both array and object types
  const indicatorList = Array.isArray(indicators) ? indicators : Object.values(indicators);

  indicatorList.forEach((indicator) => {
    if (indicator && indicator.config && indicator.config.nDrawingSlot) {
      const slotNumber = parseInt(indicator.config.nDrawingSlot, 10);
      if (!Number.isNaN(slotNumber) && slotNumber > 0) {
        usedSlots.add(slotNumber);
      }
    }
  });

  for (let slot = 1; slot <= DEFAULT_LIMITS.nDrawingSlot.max; slot += 1) {
    if (!usedSlots.has(slot)) {
      return slot.toString();
    }
  }
  return "1";
};

export const IndicatorDefaults = {
  nSignalInUse: true,
  nSignalUsedFor: "0",
  nSignalVisualization: false,
  nDrawInChart: true,
  nLogDrawingSlot: false,
  nDrawingSlot: "1",
  nSelection: "1001",
  strSymbol: "Current",
  nTimeframe: "-2",
  nSeries: "0",
  nShift: "0",
  nLineIndex: "0",
  // All 32 numeric parameters
  dParameter1: "14",
  dParameter2: "0",
  dParameter3: "0",
  dParameter4: "0",
  dParameter5: "0",
  dParameter6: "0",
  dParameter7: "0",
  dParameter8: "0",
  dParameter9: "0",
  dParameter10: "0",
  dParameter11: "0",
  dParameter12: "0",
  dParameter13: "0",
  dParameter14: "0",
  dParameter15: "0",
  dParameter16: "0",
  dParameter17: "0",
  dParameter18: "0",
  dParameter19: "0",
  dParameter20: "0",
  dParameter21: "0",
  dParameter22: "0",
  dParameter23: "0",
  dParameter24: "0",
  dParameter25: "0",
  dParameter26: "0",
  dParameter27: "0",
  dParameter28: "0",
  dParameter29: "0",
  dParameter30: "0",
  dParameter31: "0",
  dParameter32: "0",
  // String parameters
  strParameter1: "",
  strParameter2: "",
  strParameter3: "",
  strParameter4: "",
  strMt4CustomPath: "",
  strMt5CustomPath: "",
  // Value condition
  nValueCondition: "0",
  dValueLimit1: "0.00000000",
  dValueLimit2: "0.00000000",
  // Angle condition
  nAngleCondition: "0",
  dAngleLimit1: "0.00000000",
  dAngleLimit2: "0.00000000",
  // Other fields
  dForwardAdjustment: "1.00",
  nValueAnalysisFeedbackState: true,
  nSignalAnalysisFeedbackState: true,
  // Time exclusion - using single string field
  strExclusion: "00:00:00-00:00:00",
};

// ----------------------------------------------------------------------
// Section
// ----------------------------------------------------------------------
const DEFAULT_LIMITS = {
  nDrawingSlot: {
    min: 1,
    max: 64,
  },
  strSymbol: {
    maxLength: 50,
    emptyAllowed: false,
    disallowedPattern: /[<>{}[\]\\|`~@#$%^&*+=]/,
  },
  nShift: {
    min: 0,
  },
  nLineIndex: {
    min: 0,
  },
  strParameter1: {
    maxLength: 100,
    emptyAllowed: true,
  },
  strParameter2: {
    maxLength: 100,
    emptyAllowed: true,
  },
  strParameter3: {
    maxLength: 100,
    emptyAllowed: true,
  },
  strParameter4: {
    maxLength: 100,
    emptyAllowed: true,
  },
  strMt4CustomPath: {
    maxLength: 260,
    emptyAllowed: true,
  },
  strMt5CustomPath: {
    maxLength: 260,
    emptyAllowed: true,
  },
  dValueLimit1: {
    digits: 8,
  },
  dValueLimit2: {
    digits: 8,
  },
  dAngleLimit1: {
    digits: 8,
    step: 1,
    min: -25000000.0,
    max: 25000000.0,
  },
  dAngleLimit2: {
    digits: 8,
    step: 1,
    min: -25000000.0,
    max: 25000000.0,
  },
  dForwardAdjustment: {
    digits: 2,
    step: 0.01,
    min: 0.01,
  },
};

// ----------------------------------------------------------------------
// Section
// ----------------------------------------------------------------------
export const INDICATOR_CONFIGS = {
  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  1000: {
    id: "1000",
    name: "PRICE - Price directly from series",
    description: (
      <>
        The trading framework employed by the Expert Advisor Builder maintains multiple data arrays for various data series. You can choose the PRICE
        indicators to evaluate one of these data series directly. This makes it simple to detect events, such as when the close price crosses an SMA
        line. This indicator doesn't utilize any input parameters; instead, it directly forwards the data from the selected series to the indicator
        output.
        <br />
        <br />
        The available series are:
        <ul>
          <li>
            <b>Open</b>: Contains the open prices and updates once every bar.
          </li>
          <li>
            <b>High</b>: This series contains the high prices, and updates may be made multiple times for every bar.
          </li>
          <li>
            <b>Low</b>: This series contains the low prices, and updates may be made multiple times for every bar.
          </li>
          <li>
            <b>Close</b>: Contains the close prices. The final value is set at the last tick of a bar.
          </li>
          <li>
            <b>Median</b>: This series contains the median prices, and updates are made multiple times for every bar. The values are calculated with
            the following formula: (High + Low ) / 2
          </li>
          <li>
            <b>Typical</b>: This series contains the typical prices, and updates are made multiple times for every bar. The values are calculated with
            the following formula: ( High + Low + Close ) / 3
          </li>
          <li>
            <b>Weighted</b>: This series contains the weighted prices, and updates are made multiple times for every bar. The values are calculated
            with the following formula: (H+L+C)/3
          </li>
          <li>
            <b>Volume</b>: This series contains the volume information and updates at each tick.
          </li>
          <li>
            <b>Time</b>: This series contains the open time information and updates only at the beginning of a news bar.
          </li>
          <li>
            <b>Body</b>: This series contains the candle bar body size.
          </li>
          <li>
            <b>Range</b>: This series contains the candle bar range.
          </li>
          <li>
            <b>Shadow Top</b>: This series contains the candle shadow at the top.
          </li>
          <li>
            <b>Shadow Bottom</b>: This series contains the candle shadow at the bottom.
          </li>
        </ul>
        Please note that some data series require a tick-based testing process to represent their values accurately.
      </>
    ),
    image1: "/illustrations/converted/indicators/price.png",
    image1Height: "682px",
    image2: null,
    image2Height: null,
    parameters: [],
    stringParameters: [],
    defaultValues: {
      nTimeframe: "-2", // Current
      nSeries: "0", // OPEN
    },

    lineIndices: [{ value: "0", label: "Default" }],
  },
  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  1001: {
    id: "1001",
    name: "SMA - Simple Moving Average",
    description:
      "The Simple Moving Average (SMA) is a technical indicator used in trading. It calculates the average of a selected range of prices, usually closing prices, by the number of periods in that range. The SMA is an arithmetic moving average calculated by adding recent prices and dividing that figure by the number of periods in the calculation average. For example, one could add the closing price of a security for several periods and then divide this total by that same number of periods. Short-term averages respond quickly to underlying security's price changes, while long-term averages are slower to react. The SMA can aid in determining if an asset price will continue or if it will reverse a bull or bear trend.",
    image1: "/illustrations/converted/indicators/sma.png",
    image1Height: "461px",
    image2: null,
    image2Height: null,
    parameters: [
      {
        key: "dParameter1",
        label: "Period",
        description: undefined,
      },
    ],
    limits: {
      dParameter1: {
        min: 1,
      },
    },
    stringParameters: [],
    defaultValues: {
      nTimeframe: "-2", // Current
      nSeries: "0", // OPEN
      dParameter1: "14",
    },
    lineIndices: [{ value: "0", label: "Default" }],
  },
  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  1002: {
    id: "1002",
    name: "EMA - Exponential Moving Average",
    description:
      "The Exponential Moving Average (EMA) is a moving average with greater weight and significance on the most recent data points. It produces buy and sell signals based on crossovers and divergences from the historical average. Traders often use different EMA lengths, such as 10-day, 50-day, and 200-day moving averages. The EMA gives a higher weight to recent prices, while the SMA assigns equal weight to all values. It's best to use the EMA for trending markets, as it shows uptrends and downtrends when a market is strong and weak, respectively.",
    image1: "/illustrations/converted/indicators/ema.png",
    image1Height: "461px",
    image2: null,
    image2Height: null,
    parameters: [
      {
        key: "dParameter1",
        label: "Period",
        description: undefined,
      },
    ],
    limits: {
      dParameter1: {
        min: 1,
      },
    },
    stringParameters: [],
    defaultValues: {
      nTimeframe: "-2", // Current
      nSeries: "3", // CLOSE
      dParameter1: "14",
    },
    lineIndices: [{ value: "0", label: "Default" }],
  },
  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  1003: {
    id: "1003",
    name: "HMA - Hull Moving Average",
    description:
      "The Hull Moving Average (HMA) is a directional trend indicator developed by Alan Hull. It's a combination of weighted moving averages (WMAs) that prioritizes recent price changes over older ones. The HMA is dynamic yet smooth, helping identify the dominating market trend. Some traders also use the indicator to time their entry and exit signals. The HMA captures the current market's dynamics as a directional trend indicator. It relies on recent price action to determine whether the market conditions are bullish or bearish relative to historical data.",
    image1: "/illustrations/converted/indicators/hma.png",
    image1Height: "461px",
    image2: null,
    image2Height: null,
    parameters: [
      {
        key: "dParameter1",
        label: "Period",
        description: undefined,
      },
      {
        key: "dParameter2",
        label: "Moving average calculation method",
        description: "• 0 = SMA\n• 1 = EMA\n• 2 = LWMA",
      },
    ],
    limits: {
      dParameter1: {
        min: 1,
      },
      dParameter2: {
        min: 0,
        max: 2,
      },
    },
    stringParameters: [],
    defaultValues: {
      nTimeframe: "-2", // Current
      nSeries: "0", // OPEN
      dParameter1: "14",
      dParameter2: "0",
    },
    lineIndices: [{ value: "0", label: "Default" }],
  },
  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  1004: {
    id: "1004",
    name: "KAMA - Kaufman Adaptive Moving Average",
    description:
      "The Kaufman Adaptive Moving Average (KAMA) is a technical indicator developed by Perry J. Kaufman. It's an adaptive moving average that adjusts its sensitivity based on market conditions. KAMA closely follows prices when price swings are relatively small, and the noise is low. However, when price swings widen, KAMA flexibly adjusts and maintains a greater distance from the prices. The KAMA calculation involves three essential components: Efficiency Ratio (ER), Smoothing Constant (SC), and the current price. The most significant advantage of KAMA is its ability to adapt to different market conditions. During trending markets, KAMA tracks the price closely, providing timely signals. KAMA responds less sensitively in choppy or sideways markets, reducing the risk of false signals. KAMA reduces noise and produces a smoother line compared to other moving averages.",
    image1: "/illustrations/converted/indicators/kama.png",
    image1Height: "461px",
    image2: null,
    image2Height: null,
    parameters: [
      {
        key: "dParameter1",
        label: "Period",
        description: undefined,
      },
      {
        key: "dParameter2",
        label: "Fastest",
        description: undefined,
      },
      {
        key: "dParameter3",
        label: "Slowest",
        description: undefined,
      },
    ],
    limits: {
      dParameter1: {
        min: 1,
      },
      dParameter2: {
        digits: 1,
        step: 0.1,
        min: 0.1,
      },
      dParameter3: {
        digits: 1,
        step: 0.1,
        min: 0.1,
      },
    },
    stringParameters: [],
    defaultValues: {
      nTimeframe: "-2", // Current
      nSeries: "3", // CLOSE
      dParameter1: "10",
      dParameter2: "2.0",
      dParameter3: "30.0",
    },
    lineIndices: [{ value: "0", label: "Default" }],
  },
  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  1005: {
    id: "1005",
    name: "LWMA - Linear Weighted Moving Average",
    description:
      "The Linear Weighted Moving Average (LWMA) is a moving average that assigns a higher weight to recent price data. The most recent price has the highest weighting, and each prior price has progressively less weight. The weights drop linearly. LWMAs are quicker to react to price changes than simple moving averages (SMA) and exponential moving averages (EMA). Traders use an LWMA to define the price trend and reversals more clearly, provide trade signals based on crossovers, and indicate potential support or resistance areas. Traders who want a moving average with less lag than an SMA may utilize an LWMA.",
    image1: "/illustrations/converted/indicators/lwma.png",
    image1Height: "461px",
    image2: null,
    image2Height: null,
    parameters: [
      {
        key: "dParameter1",
        label: "Period",
        description: undefined,
      },
    ],
    limits: {
      dParameter1: {
        min: 1,
      },
    },
    stringParameters: [],
    defaultValues: {
      nTimeframe: "-2", // Current
      nSeries: "3", // CLOSE
      dParameter1: "14",
    },
    lineIndices: [{ value: "0", label: "Default" }],
  },
  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  1006: {
    id: "1006",
    name: "SMMA - Smoothed Moving Average",
    description:
      "The Smoothed Moving Average (SMMA) is a moving average that assigns equal weight to recent and historical prices. Unlike the Simple Moving Average (SMA), which removes the oldest price data as a new price is added, the SMMA keeps all price data in its calculation. Although the oldest price data is not removed, it has less impact on the moving average. The SMMA uses a more extended period to determine the average and assigns weight to price data while calculating the average. This indicator is popular amongst many traders due to its smoothing-out function and ability. The SMMA can remove short-term fluctuations and unimportant movements associated with the current trend. Traders need to remember that the SMMA is a function of weight in connection with the price or length of the average.",
    image1: "/illustrations/converted/indicators/smma.png",
    image1Height: "461px",
    image2: null,
    image2Height: null,
    parameters: [
      {
        key: "dParameter1",
        label: "Period",
        description: undefined,
      },
    ],
    limits: {
      dParameter1: {
        min: 1,
      },
    },
    stringParameters: [],
    defaultValues: {
      nTimeframe: "-2", // Current
      nSeries: "3", // CLOSE
      dParameter1: "14",
    },
    lineIndices: [{ value: "0", label: "Default" }],
  },
  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  1007: {
    id: "1007",
    name: "BB - Bollinger Bands",
    description:
      "Bollinger Bands are a technical analysis tool developed by John Bollinger. They consist of three lines: a simple moving average (middle band) and an upper and lower band typically set at two standard deviations from the moving average. The bands adjust to market volatility, widening during high volatility and contracting during low volatility. When prices touch the upper band, it may indicate an overbought market; when prices feel the lower band, it may indicate an oversold market. These bands identify potential buy and sell signals and possible trend reversals.",
    image1: "/illustrations/converted/indicators/bb.png",
    image1Height: "547px",
    image2: null,
    image2Height: null,
    parameters: [
      {
        key: "dParameter1",
        label: "Period",
        description: undefined,
      },
      {
        key: "dParameter2",
        label: "Bands deviations",
        description: undefined,
      },
    ],
    limits: {
      dParameter1: {
        min: 1,
      },
      dParameter2: {
        digits: 1,
        step: 0.1,
        min: 0.1,
      },
    },
    stringParameters: [],
    defaultValues: {
      nTimeframe: "-2", // Current
      nSeries: "3", // CLOSE
      dParameter1: "14",
      dParameter2: "2.0",
    },
    lineIndices: [
      { value: "0", label: "Main" },
      { value: "1", label: "Upper" },
      { value: "2", label: "Lower" },
    ],
  },
  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  1008: {
    id: "1008",
    name: "LREG - Linear Regression",
    description:
      "Linear Regression is a technical analysis tool that statistically models price and time relationships. It plots the ending value of a Linear Regression Line for a specified number of bars, showing where the price is expected to be. This makes it more responsive than a moving average as it plots where a Linear Regression Line would expect the price to be instead of plotting an average of past price action. Traders can use it to identify key price points - entry, stop loss, and exit prices. It's a trend-identifying and trend-following indicator. This tool helps predict future prices based on historical data.",
    image1: "/illustrations/converted/indicators/lreg.png",
    image1Height: "461px",
    image2: null,
    image2Height: null,
    parameters: [
      {
        key: "dParameter1",
        label: "Period",
        description: undefined,
      },
    ],
    limits: {
      dParameter1: {
        min: 1,
      },
    },
    stringParameters: [],
    defaultValues: {
      nTimeframe: "-2", // Current
      nSeries: "0", // OPEN
      dParameter1: "14",
    },
    lineIndices: [{ value: "0", label: "Default" }],
  },
  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  1009: {
    id: "1009",
    name: "DON - Donchian Channel",
    description: (
      <>
        Donchian Channels are a technical analysis tool that measures a market's volatility and identifies potential breakouts or overbought/oversold
        conditions. Developed by Richard Donchian, they consist of three lines: an upper band marking the highest price over a certain period, a lower
        band marking the lowest price, and a middle band representing the average of the two. The bands create a visual price map over time,
        indicating the extent of bullishness and bearishness. They help traders establish whether the dominant trend direction in a market is bullish
        or bearish and serve as a guide for setting trade entries and exits. This tool is highly versatile and can define trading ranges and upcoming
        trend activity.
        <br />
        <br />
        <b>Note</b>: <b>Every tick</b> as model and <b>Close</b> as series recommended.
      </>
    ),
    image1: "/illustrations/converted/indicators/don.png",
    image1Height: "461px",
    image2: null,
    image2Height: null,
    parameters: [
      {
        key: "dParameter1",
        label: "Period",
        description: undefined,
      },
    ],
    limits: {
      dParameter1: {
        min: 1,
      },
    },
    stringParameters: [],
    defaultValues: {
      nTimeframe: "-2", // Current
      nSeries: "3", // CLOSE
      dParameter1: "14",
    },
    lineIndices: [
      { value: "0", label: "Upper channel" },
      { value: "1", label: "Lower channel" },
      { value: "2", label: "Middle channel" },
    ],
  },
  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  1010: {
    id: "1010",
    name: "PSS - Parabolic Sar Stop",
    description:
      "The Parabolic SAR (Stop and Reverse) is a technical analysis tool developed by J. Wells Wilder. It helps traders determine trend direction and potential reversals in price. The indicator uses a trailing stop and reverse method, appearing on a chart as a series of dots either above or below an asset's price, depending on the price's direction. A dot below the price indicates an upward trend, and a dot above the price suggests a downward trend. When the dots flip, it signals a potential change in price direction. However, a reversal signal in the SAR does not necessarily mean a reversal in the price. Traders use it to spot trend reversals and to identify suitable exit and entry points.",
    image1: "/illustrations/converted/indicators/pss.png",
    image1Height: "461px",
    image2: null,
    image2Height: null,
    parameters: [
      {
        key: "dParameter1",
        label: "Step",
        description: undefined,
      },
      {
        key: "dParameter2",
        label: "Maximum",
        description: undefined,
      },
    ],
    limits: {
      dParameter1: {
        digits: 2,
        step: 0.01,
        min: 0.01,
      },
      dParameter2: {
        digits: 2,
        step: 0.01,
        min: 0.01,
      },
    },
    stringParameters: [],
    defaultValues: {
      nTimeframe: "-2", // Current
      nSeries: "3", // CLOSE
      dParameter1: "0.02",
      dParameter2: "0.2",
    },
    lineIndices: [{ value: "0", label: "Default" }],
  },
  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  1011: {
    id: "1011",
    name: "ENV - Envelopes",
    description: (
      <>
        Envelopes are technical indicators plotted over a price chart with upper and lower bounds. They are typically generated by a simple moving
        average and a predetermined distance above and below the moving average. The most common example of an envelope is a moving average envelope.
        Envelopes help traders and investors identify extreme overbought and oversold conditions and trading ranges. The security is considered
        overbought when the price reaches the upper bound and a sell signal is generated. Conversely, when the price comes to the lower bound, the
        security is considered oversold, and a buy signal is generated. These strategies are based on mean reversion principles.
        <br />
        <br />
        <b>Note</b>: <b>Close</b> as series recommended.
      </>
    ),
    image1: "/illustrations/converted/indicators/env.png",
    image1Height: "557px",
    image2: null,
    image2Height: null,
    parameters: [
      {
        key: "dParameter1",
        label: "Period",
        description: undefined,
      },
      {
        key: "dParameter2",
        label: "Moving average calculation method",
        description: "• 0 = SMA\n• 1 = EMA\n• 2 = LWMA",
      },
      {
        key: "dParameter3",
        label: "Deviation",
        description: undefined,
      },
    ],
    limits: {
      dParameter1: {
        min: 1,
      },
      dParameter2: {
        min: 1,
        max: 2,
      },
      dParameter3: {
        digits: 2,
        step: 0.01,
        min: 0.01,
      },
    },
    stringParameters: [],
    defaultValues: {
      nTimeframe: "-2", // Current
      nSeries: "3", // CLOSE
      dParameter1: "13",
      dParameter2: "0",
      dParameter3: "0.2",
    },
    lineIndices: [
      { value: "0", label: "Lower envelope" },
      { value: "1", label: "Main line" },
      { value: "2", label: "Upper envelope" },
    ],
  },
  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  1012: {
    id: "1012",
    name: "IKH - Ichimoku Kinko Hyo",
    description: (
      <>
        The Ichimoku Kinko Hyo, or Ichimoku for short, is a technical indicator to gauge momentum, future support, and resistance areas. It consists
        of five lines: the Tenkan-Sen, Kijun-Sen, Senkou span A, Senkou span B, and Chikou span. These lines help identify key support and resistance
        levels signal reversals and can be used as trailing stop-loss points. The cloud or Kumo, formed by Senkou span A and B, predicts future
        support and resistance levels. The indicator is best used in conjunction with other forms of technical analysis. Despite its complex
        appearance, it provides valuable information once understood.
        <br />
        <br />
        <b>Note</b>: <b>Every tick</b> as model and <b>Close</b> as series recommended.
      </>
    ),
    image1: "/illustrations/converted/indicators/ikh.png",
    image1Height: "821px",
    image2: null,
    image2Height: null,
    parameters: [
      {
        key: "dParameter1",
        label: "Tenkan-sen",
        description: undefined,
      },
      {
        key: "dParameter2",
        label: "Kijun-sen",
        description: undefined,
      },
      {
        key: "dParameter3",
        label: "Senkou span B",
        description: undefined,
      },
    ],
    limits: {
      dParameter1: {
        min: 1,
      },
      dParameter2: {
        min: 1,
      },
      dParameter3: {
        min: 1,
      },
    },
    stringParameters: [],
    defaultValues: {
      nTimeframe: "-2", // Current
      nSeries: "3", // CLOSE
      dParameter1: "9",
      dParameter2: "26",
      dParameter3: "52",
    },
    lineIndices: [
      { value: "0", label: "Tenkan-sen" },
      { value: "1", label: "Kijun-sen" },
      { value: "2", label: "Senkou span A" },
      { value: "3", label: "Senkou span B" },
      { value: "4", label: "Chikou span" },
    ],
  },
  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  1013: {
    id: "1013",
    name: "PRO - Price Prominence",
    description: (
      <>
        The prominence indicator provides the most recent local high or low whenever the price is in a short-term falling or rising trend,
        respectively. It quantifies the difference between a local low or high and the surrounding price, outputting the latest price that must be
        exceeded to reach an even lower or higher price. This indicator helps identify the most recent support or resistance levels. Notably, the
        detection changes depending on whether the current opening price is smaller or larger than the previous closing price.
        <br />
        <br />
        <b>Note</b>: <b>Every tick</b> as model and <b>Close</b> as series recommended.
      </>
    ),
    image1: "/illustrations/converted/indicators/pro.png",
    image1Height: "831px",
    image2: null,
    image2Height: null,
    parameters: [
      {
        key: "dParameter1",
        label: "Period",
        description: undefined,
      },
    ],
    limits: {
      dParameter1: {
        min: 1,
      },
    },
    stringParameters: [],
    defaultValues: {
      nTimeframe: "-2", // Current
      nSeries: "0", // OPEN
      dParameter1: "14",
    },
    lineIndices: [{ value: "0", label: "Default" }],
  },
  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  1014: {
    id: "1014",
    name: "PIV - Pivot Points",
    description: (
      <>
        Pivot Points are a technical analysis indicator used to determine the market's overall trend over different timeframes. The pivot point is the
        average intraday high and low and the closing price from the previous trading day. Trading above the pivot point indicates ongoing bullish
        sentiment while trading below the pivot point indicates bearish sentiment. The indicator also includes other support and resistance levels
        projected based on the pivot point calculation. These levels help traders see where the price could experience support or resistance. If the
        price moves through these levels, it indicates the price is trending in that direction. Pivot points identify trends and reversals, mainly in
        equities, commodities, and forex markets.
        <br />
        <br />
        <b>Note</b>: <b>Every tick</b> as model, <b>Close</b> as series, and <b>D1</b> as the timeframe recommended.
      </>
    ),
    image1: "/illustrations/converted/indicators/piv.png",
    image1Height: "1029px",
    image2: null,
    image2Height: null,
    parameters: [],
    stringParameters: [],
    defaultValues: {
      nTimeframe: "6", // D1
      nSeries: "0", // OPEN
    },
    lineIndices: [
      { value: "0", label: "Pivot point" },
      { value: "1", label: "Support 1" },
      { value: "2", label: "Support 2" },
      { value: "3", label: "Support 3" },
      { value: "4", label: "Resistance 1" },
      { value: "5", label: "Resistance 2" },
      { value: "6", label: "Resistance 3" },
    ],
  },
  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  1015: {
    id: "1015",
    name: "STR - Supertrend",
    description:
      "The Supertrend Indicator is a technical analysis tool developed by Olivier Seban. It helps identify market trends and potential entry and exit points. The indicator combines the average true range (ATR) with a multiplier to calculate its value. This value is then added to or subtracted from the asset's closing price to plot the supertrend line. When the price is above this line, the market is considered to be trending up, signaling a buying opportunity. Conversely, the market is trending downward when the price is below the line, indicating a sell opportunity. The Supertrend Indicator is notably helpful in markets when prices go in a clear direction, upward or downward. It is often used with other technical indicators for a more comprehensive trading strategy.",
    image1: "/illustrations/converted/indicators/str.png",
    image1Height: "909px",
    image2: null,
    image2Height: null,
    parameters: [
      {
        key: "dParameter1",
        label: "Multiplier",
        description: undefined,
      },
      {
        key: "dParameter2",
        label: "CCI period",
        description: undefined,
      },
      {
        key: "dParameter3",
        label: "ATR period",
        description: undefined,
      },
    ],
    limits: {
      dParameter1: {
        min: 1,
      },
      dParameter2: {
        min: 1,
      },
      dParameter3: {
        min: 1,
      },
    },
    stringParameters: [],
    defaultValues: {
      nTimeframe: "-2", // Current
      nSeries: "0", // OPEN
      dParameter1: "2",
      dParameter2: "50",
      dParameter3: "5",
    },
    lineIndices: [
      { value: "0", label: "Supertrend" },
      { value: "1", label: "Upper/lower in use" },
    ],
  },
  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  1016: {
    id: "1016",
    name: "CUS - Custom indicator",
    description:
      "Custom indicator allows you to use your own external indicators by specifying the path to the indicator file. The chart line will be drawn in the same window.",
    image1: null,
    image1Height: null,
    image2: null,
    image2Height: null,
    parameters: [
      {
        key: "dParameter1",
        label: "Custom parameter 1",
        description: undefined,
      },
      {
        key: "dParameter2",
        label: "Custom parameter 2",
        description: undefined,
      },
      {
        key: "dParameter3",
        label: "Custom parameter 3",
        description: undefined,
      },
      {
        key: "dParameter4",
        label: "Custom parameter 4",
        description: undefined,
      },
      {
        key: "dParameter5",
        label: "Custom parameter 5",
        description: undefined,
      },
      {
        key: "dParameter6",
        label: "Custom parameter 6",
        description: undefined,
      },
      {
        key: "dParameter7",
        label: "Custom parameter 7",
        description: undefined,
      },
      {
        key: "dParameter8",
        label: "Custom parameter 8",
        description: undefined,
      },
      {
        key: "dParameter9",
        label: "Custom parameter 9",
        description: undefined,
      },
      {
        key: "dParameter10",
        label: "Custom parameter 10",
        description: undefined,
      },
      {
        key: "dParameter11",
        label: "Custom parameter 11",
        description: undefined,
      },
      {
        key: "dParameter12",
        label: "Custom parameter 12",
        description: undefined,
      },
      {
        key: "dParameter13",
        label: "Custom parameter 13",
        description: undefined,
      },
      {
        key: "dParameter14",
        label: "Custom parameter 14",
        description: undefined,
      },
      {
        key: "dParameter15",
        label: "Custom parameter 15",
        description: undefined,
      },
      {
        key: "dParameter16",
        label: "Custom parameter 16",
        description: undefined,
      },
      {
        key: "dParameter17",
        label: "Custom parameter 17",
        description: undefined,
      },
      {
        key: "dParameter18",
        label: "Custom parameter 18",
        description: undefined,
      },
      {
        key: "dParameter19",
        label: "Custom parameter 19",
        description: undefined,
      },
      {
        key: "dParameter20",
        label: "Custom parameter 20",
        description: undefined,
      },
      {
        key: "dParameter21",
        label: "Custom parameter 21",
        description: undefined,
      },
      {
        key: "dParameter22",
        label: "Custom parameter 22",
        description: undefined,
      },
      {
        key: "dParameter23",
        label: "Custom parameter 23",
        description: undefined,
      },
      {
        key: "dParameter24",
        label: "Custom parameter 24",
        description: undefined,
      },
      {
        key: "dParameter25",
        label: "Custom parameter 25",
        description: undefined,
      },
      {
        key: "dParameter26",
        label: "Custom parameter 26",
        description: undefined,
      },
      {
        key: "dParameter27",
        label: "Custom parameter 27",
        description: undefined,
      },
      {
        key: "dParameter28",
        label: "Custom parameter 28",
        description: undefined,
      },
      {
        key: "dParameter29",
        label: "Custom parameter 29",
        description: undefined,
      },
      {
        key: "dParameter30",
        label: "Custom parameter 30",
        description: undefined,
      },
      {
        key: "dParameter31",
        label: "Custom parameter 31",
        description: undefined,
      },
      {
        key: "dParameter32",
        label: "Custom parameter 32",
        description: undefined,
      },
    ],
    limits: {
      dParameter1: {
        digits: 8,
      },
      dParameter2: {
        digits: 8,
      },
      dParameter3: {
        digits: 8,
      },
      dParameter4: {
        digits: 8,
      },
      dParameter5: {
        digits: 8,
      },
      dParameter6: {
        digits: 8,
      },
      dParameter7: {
        digits: 8,
      },
      dParameter8: {
        digits: 8,
      },
      dParameter9: {
        digits: 8,
      },
      dParameter10: {
        digits: 8,
      },
      dParameter11: {
        digits: 8,
      },
      dParameter12: {
        digits: 8,
      },
      dParameter13: {
        digits: 8,
      },
      dParameter14: {
        digits: 8,
      },
      dParameter15: {
        digits: 8,
      },
      dParameter16: {
        digits: 8,
      },
      dParameter17: {
        digits: 8,
      },
      dParameter18: {
        digits: 8,
      },
      dParameter19: {
        digits: 8,
      },
      dParameter20: {
        digits: 8,
      },
      dParameter21: {
        digits: 8,
      },
      dParameter22: {
        digits: 8,
      },
      dParameter23: {
        digits: 8,
      },
      dParameter24: {
        digits: 8,
      },
      dParameter25: {
        digits: 8,
      },
      dParameter26: {
        digits: 8,
      },
      dParameter27: {
        digits: 8,
      },
      dParameter28: {
        digits: 8,
      },
      dParameter29: {
        digits: 8,
      },
      dParameter30: {
        digits: 8,
      },
      dParameter31: {
        digits: 8,
      },
      dParameter32: {
        digits: 8,
      },
    },
    stringParameters: [
      {
        key: "strParameter1",
        label: "String custom parameter 1",
        description: undefined,
      },
      {
        key: "strParameter2",
        label: "String custom parameter 2",
        description: undefined,
      },
      {
        key: "strParameter3",
        label: "String custom parameter 3",
        description: undefined,
      },
      {
        key: "strParameter4",
        label: "String custom parameter 4",
        description: undefined,
      },
      {
        key: "strMt4CustomPath",
        label: "MetaTrader4 path to indicator file",
        description: 'Relative path starting from the "Indicators" folder in "Data Folder > MQL4".',
      },
      {
        key: "strMt5CustomPath",
        label: "MetaTrader5 path to indicator file",
        description: 'Relative path starting from the "Indicators" folder in "Data Folder > MQL5".',
      },
    ],
    defaultValues: {
      nTimeframe: "-2", // Current
      nSeries: "0", // OPEN
      dParameter1: "0.00000000",
      dParameter2: "0.00000000",
      dParameter3: "0.00000000",
      dParameter4: "0.00000000",
      dParameter5: "0.00000000",
      dParameter6: "0.00000000",
      dParameter7: "0.00000000",
      dParameter8: "0.00000000",
      dParameter9: "0.00000000",
      dParameter10: "0.00000000",
      dParameter11: "0.00000000",
      dParameter12: "0.00000000",
      dParameter13: "0.00000000",
      dParameter14: "0.00000000",
      dParameter15: "0.00000000",
      dParameter16: "0.00000000",
      dParameter17: "0.00000000",
      dParameter18: "0.00000000",
      dParameter19: "0.00000000",
      dParameter20: "0.00000000",
      dParameter21: "0.00000000",
      dParameter22: "0.00000000",
      dParameter23: "0.00000000",
      dParameter24: "0.00000000",
      dParameter25: "0.00000000",
      dParameter26: "0.00000000",
      dParameter27: "0.00000000",
      dParameter28: "0.00000000",
      dParameter29: "0.00000000",
      dParameter30: "0.00000000",
      dParameter31: "0.00000000",
      dParameter32: "0.00000000",
      strMt4CustomPath: "\\Folder\\MyExternalIndicator.ex4",
      strMt5CustomPath: "Folder\\MyExternalIndicator",
    },
    lineIndices: [],
  },
  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  1017: {
    id: "1017",
    name: "IRAFI - Range finder (same window)",
    description: <>
      The Range Finder is an advanced technical analysis indicator that automatically identifies and tracks optimal trading ranges by analyzing price action patterns and market structure. It employs a sophisticated ZigZag algorithm with ATR-based swing filtering to detect significant price pivots, then evaluates multiple range configurations to find the most statistically robust trading boundaries.
      <br />
      <br />
      The indicator calculates upper and lower range limits with customizable buffer zones and breakout thresholds, dynamically adapting to market conditions through a weighted scoring system that considers touch point distribution, price excursion balance, and range age. Key features include Gini coefficient analysis for touch point equality, median swing height calculations using Fibonacci multipliers, and automatic range updates based on breakout events or statistical deterioration. 
      <br />
      <br />
      The Range Finder provides traders with clear visual boundaries for range-bound trading strategies, offering both current price position within the range (0-100%) and discrete range slots for systematic entry and exit decisions. It's particularly effective in consolidating markets where price oscillates between well-defined support and resistance levels, helping traders identify optimal entry points near range boundaries and potential breakout opportunities when price moves beyond established thresholds.
      <br />
      <br />
      This tool is available in two formats: as an oscillator and as a standard indicator. The oscillator version displays the selected line in a separate window, typically below the main chart. The indicator version renders the line directly within the main chart window, overlaying price data. 
      <br />
      <br />
      The following lines are available, along with their recommended display windows:
      <ul style={{ listStyleType: "disc" }}>
        <li style={{ marginBottom: "10px" }}>
          <b>Current price relative to range:</b> Separate window
        </li>
        <li style={{ marginBottom: "10px" }}>
          <b>Current range slot:</b> Separate window
        </li>
        <li style={{ marginBottom: "10px" }}>
          <b>Upper breakout price:</b> Same window
        </li>
        <li style={{ marginBottom: "10px" }}>
          <b>Upper price:</b> Same window
        </li>
        <li style={{ marginBottom: "10px" }}>
          <b>Upper buffer price:</b> Same window
        </li>
        <li style={{ marginBottom: "10px" }}>
          <b>Lower buffer price:</b> Same window
        </li>
        <li style={{ marginBottom: "10px" }}>
          <b>Lower price:</b> Same window
        </li>
        <li style={{ marginBottom: "10px" }}>
          <b>Lower breakout price:</b> Same window
        </li>
      </ul>    
    </>,
    image1: "/illustrations/converted/indicators/rafi_1.png",
    image1Height: "663px",
    image2: "/illustrations/converted/indicators/rafi_2.png",
    image2Height: "663px",
    parameters: [
      {
        key: "dParameter1",
        label: "Minimum range length",
        description: undefined,
      },
      {
        key: "dParameter2",
        label: "Maximum range length",
        description: undefined,
      },
      {
        key: "dParameter3",
        label: "Step size for range testing",
        description: undefined,
      },
      {
        key: "dParameter4",
        label: "Minimum range height ",
        description: "Multiplies with ZigZag median swing height.",
      },
      {
        key: "dParameter5",
        label: "Maximum range height",
        description: "Multiplies with ZigZag median swing height.",
      },
      {
        key: "dParameter6",
        label: "Buffer height",
        description: "<b>>0</b>: In percent of range.\n<b><0</b>: Fibonacci level * ZigZag median swing height\n\nFibonacci levels:\n<b>-0</b> = 5.6%\n<b>-1</b> = 9.0%\n<b>-2</b> = 14.6%\n<b>-3</b> = 23.6%\n<b>-4</b> = 38.2%\n<b>-5</b> = 50.0%\n<b>-6</b> = 61.8%\n<b>-7</b> = 78.6%\n<b>-8</b> = 88.6%\n<b>-9</b> = 100.0%\n<b>-10</b> = 161.8%\n<b>-11</b> = 261.8%\n<b>-12</b> = 423.6%\n<b>-13</b> = 685.4%",
      },
      {
        key: "dParameter7",
        label: "Breakout height",
        description: "<b>>0</b>: In percent of range.\n<b><0</b>: Fibonacci level * ZigZag median swing height\n\nFibonacci levels:\n<b>-0</b> = 5.6%\n<b>-1</b> = 9.0%\n<b>-2</b> = 14.6%\n<b>-3</b> = 23.6%\n<b>-4</b> = 38.2%\n<b>-5</b> = 50.0%\n<b>-6</b> = 61.8%\n<b>-7</b> = 78.6%\n<b>-8</b> = 88.6%\n<b>-9</b> = 100.0%\n<b>-10</b> = 161.8%\n<b>-11</b> = 261.8%\n<b>-12</b> = 423.6%\n<b>-13</b> = 685.4%",
      },
      {
        key: "dParameter8",
        label: "Minimum holding bars",
        description: "Specifies the minimum duration a range remains fixed before initiating a search for a new one.",
      },
      {
        key: "dParameter9",
        label: "Draw ranges",
        description: "Activates or deactivates the built-in drawing tool for defined ranges.",
      },
    ],
    limits: {
      dParameter1: {
        digits: 0,
        min: 0,
      },
      dParameter2: {
        digits: 0,
        min: 0,
      },
      dParameter3: {
        digits: 0,
        min: 0,
      },
      dParameter4: {
        digits: 1,
        step: 0.1,
        min: 1.0,
      },
      dParameter5: {
        digits: 1,
        step: 0.1,
        min: 1.0,
      },
      dParameter6: {
        digits: 0,
      },
      dParameter7: {
        digits: 0,
      },
      dParameter8: {
        digits: 0,
        min: 0,
      },
      dParameter9: {
        digits: 0,
        min: 0,
        max: 1,
      },
    },
    stringParameters: [],
    defaultValues: {
      nTimeframe: "-2", // Current
      nSeries: "0", // OPEN
      dParameter1: "50",
      dParameter2: "300",
      dParameter3: "25",
      dParameter4: "3.5",
      dParameter5: "6.0",
      dParameter6: "-9",
      dParameter7: "-10",
      dParameter8: "50",
      dParameter9: "1",
    },
    lineIndices: [
      { value: "0", label: "Current price relative to range" },
      { value: "1", label: "Current range slot" },
      { value: "2", label: "Upper breakout price" },
      { value: "3", label: "Upper price" },
      { value: "4", label: "Upper buffer price" },
      { value: "5", label: "Lower buffer price" },
      { value: "6", label: "Lower price" },
      { value: "7", label: "Lower breakout price" },
    ],
  },
  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  1018: {
    id: "1018",
    name: "IZZFI - ZigZag Fibonacci (same window)",
    description: <>
      The ZigZag indicator is a dynamic technical analysis tool that identifies significant price swings by filtering out minor price movements and market noise. It uses an ATR-based adaptive algorithm to determine minimum swing thresholds, ensuring the indicator automatically adjusts to different market volatilities and timeframes. The indicator employs a three-pass algorithm: first identifying local extremes using depth parameters, then removing closely spaced extremes through backstep filtering, and finally building an alternating sequence of highs and lows that meet minimum swing size requirements. 
      <br />
      <br />
      Key features include automatic Fibonacci retracement level calculations based on recent swings, with support for extended Fibonacci ratios from 5.6% to 423.6%. The indicator calculates median swing heights across all detected pivots, providing a statistical measure of typical market movement ranges. Visual components include color-coded swing lines (green for upswings, red for downswings) and optional Fibonacci level overlays with percentage labels. 
      <br />
      <br />
      The ZigZag is particularly valuable for identifying trend reversals, measuring retracement levels, and determining optimal entry and exit points based on swing structure. It helps traders visualize the underlying market rhythm by connecting significant peaks and troughs while ignoring insignificant fluctuations, making it ideal for swing trading strategies and Elliott Wave analysis.
      <br />
      <br />
      This tool is available in two formats: as an oscillator and as a standard indicator. The oscillator version displays the selected line in a separate window, typically below the main chart. The indicator version renders the line directly within the main chart window, overlaying price data. 
      <br />
      <br />
      The following lines are available, along with their recommended display windows:
      <ul style={{ listStyleType: "disc" }}>
        <li style={{ marginBottom: "10px" }}>
          <b>ZigZag median swing height:</b> Separate window
        </li>
        <li style={{ marginBottom: "10px" }}>
          <b>Fibonacci level value:</b> Same window
        </li>
      </ul>    
    </>,
    image1: "/illustrations/converted/indicators/zzfi_1.png",
    image1Height: "663px",
    image2: "/illustrations/converted/indicators/zzfi_2.png",
    image2Height: "663px",
    parameters: [
      {
        key: "dParameter1",
        label: "Period",
        description: "Period for ZigZag analysis (bars to analyze)",
      },
      {
        key: "dParameter2",
        label: "Sensitivity",
        description: "Smaller depth for more sensitivity",
      },
      {
        key: "dParameter3",
        label: "Minimum swing height",
        description: "Multiplier to increase/decrease minimum swing requirement",
      },
      {
        key: "dParameter4",
        label: "Backstep height",
        description: "Less backstep for more ZigZag points.",
      },
      {
        key: "dParameter5",
        label: "Swing for Fibonacci feature",
        description: "ZigZag swing from which the Fibonacci retracement begins.",
      },
      {
        key: "dParameter6",
        label: "Fibonacci level",
        description: "Fibonacci level value to return.\n\nFibonacci levels:\n<b>0</b> = 5.6%\n<b>1</b> = 9.0%\n<b>2</b> = 14.6%\n<b>3</b> = 23.6%\n<b>4</b> = 38.2%\n<b>5</b> = 50.0%\n<b>6</b> = 61.8%\n<b>7</b> = 78.6%\n<b>8</b> = 88.6%\n<b>9</b> = 100.0%\n<b>10</b> = 161.8%\n<b>11</b> = 261.8%\n<b>12</b> = 423.6%\n<b>13</b> = 685.4%",
      },
      {
        key: "dParameter7",
        label: "Draw flag",
        description: "Activates or deactivates the built-in drawing tool.\n\nOptions are:\n<b>0</b> = Nothing drawn\n<b>1</b> = Fibonacci retracements drawn\n<b>2</b> = ZigZag drawn\n<b>3</b> = Fibonacci retracements and ZigZag drawn",
      },
    ],
    limits: {
      dParameter1: {
        digits: 0,
        min: 0,
      },
      dParameter2: {
        digits: 0,
        min: 0,
      },
      dParameter3: {
        digits: 1,
        step: 0.1,
        min: 1.0,
      },
      dParameter4: {
        digits: 0,
        min: 0,
      },
      dParameter5: {
        digits: 0,
        min: 0,
      },
      dParameter6: {
        digits: 0,
        min: 0,
      },
      dParameter7: {
        digits: 0,
        min: 0,
        max: 3,
      },
    },
    stringParameters: [],
    defaultValues: {
      nTimeframe: "-2", // Current
      nSeries: "3", // Close
      dParameter1: "3000",
      dParameter2: "6",
      dParameter3: "5.0",
      dParameter4: "4",
      dParameter5: "1",
      dParameter6: "10",
      dParameter7: "3",
    },
    lineIndices: [
      { value: "0", label: "ZigZag median swing height" },
      { value: "1", label: "Fibonacci level value" },
      { value: "2", label: "Direction of selected swing" },
    ],
  },
};

// ----------------------------------------------------------------------
// Section
// ----------------------------------------------------------------------
const OSCILLATOR_CONFIGS = {
  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  2000: {
    id: "2000",
    name: "RSI - Relative Strength Index",
    description:
      "The Relative Strength Index (RSI) is a momentum oscillator developed by J. Welles Wilder. It measures the speed and change of price movements, oscillating between zero and 100. Traditionally, an RSI above 70 indicates an overbought condition, suggesting a potential sell opportunity, while an RSI below 30 indicates an oversold condition, suggesting a potential buy opportunity. The RSI can also identify potential price reversals and is often used with other technical oscillators for a more comprehensive trading strategy. However, during strong trends, the RSI may remain in overbought or oversold conditions for extended periods. It's a valuable tool for traders to gauge market momentum and make informed trading decisions.",
    image1: "/illustrations/converted/indicators/rsi.png",
    image1Height: "831px",
    image2: null,
    image2Height: null,
    parameters: [
      {
        key: "dParameter1",
        label: "Period",
        description: undefined,
      },
      {
        key: "dParameter2",
        label: "RSI Method",
        description: "• 0 = Exponential\n• 1 = Original",
      },
    ],
    limits: {
      dParameter1: {
        min: 1,
        max: 2000,
      },
      dParameter2: {
        min: 0,
        max: 1,
      },
    },
    stringParameters: [],
    defaultValues: {
      nTimeframe: "-2", // Current
      nSeries: "3", // CLOSE
      dParameter1: "14",
      dParameter2: "0",
    },
    lineIndices: [{ value: "0", label: "Default" }],
  },
  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  2001: {
    id: "2001",
    name: "RVI - Relative Volatility Index",
    description:
      "The Relative Volatility Index (RVI) is a volatility oscillator developed by Donald Dorsey. It measures the standard deviation of high and low prices over a defined range of periods. The RVI ranges from 0 to 100. When the RVI is above 50, it indicates that the volatility is to the upside, confirming a potential buy signal. Conversely, when the RVI is below 50, the volatility is to the downside, demonstrating a potential sell signal. The RVI is not meant to be used as a standalone oscillator for trading. It's most widely used in conjunction with moving average crossover signals. It does an exceptional job of measuring market strength.",
    image1: "/illustrations/converted/indicators/rvi.png",
    image1Height: "831px",
    image2: null,
    image2Height: null,
    parameters: [
      {
        key: "dParameter1",
        label: "Period",
        description: undefined,
      },
    ],
    limits: {
      dParameter1: {
        min: 1,
      },
    },
    stringParameters: [],
    defaultValues: {
      nTimeframe: "-2", // Current
      nSeries: "3", // CLOSE
      dParameter1: "14",
    },
    lineIndices: [
      { value: "0", label: "Main line" },
      { value: "1", label: "Signal line" },
    ],
  },
  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  2002: {
    id: "2002",
    name: "ROC - Rate of Change",
    description:
      "The Rate of Change (ROC) is a momentum oscillator that measures the percentage change in price between the current price and the price a certain number of periods ago. The ROC oscillator is plotted against zero, with the oscillator moving upwards into positive territory if price changes are to the upside and moving into negative territory if price changes are to the downside. The oscillator can spot divergences, overbought and oversold conditions, and centerline crossovers. A rising ROC above zero typically confirms an uptrend, while a falling ROC below zero indicates a downtrend. When the price is consolidating, the ROC will hover near zero. It's a valuable tool for traders to gauge market momentum and make informed trading decisions.",
    image1: "/illustrations/converted/indicators/roc.png",
    image1Height: "831px",
    image2: null,
    image2Height: null,
    parameters: [
      {
        key: "dParameter1",
        label: "Period",
        description: undefined,
      },
    ],
    limits: {
      dParameter1: {
        min: 1,
      },
    },
    stringParameters: [],
    defaultValues: {
      nTimeframe: "-2", // Current
      nSeries: "3", // CLOSE
      dParameter1: "14",
    },
    lineIndices: [{ value: "0", label: "Default" }],
  },
  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  2003: {
    id: "2003",
    name: "ATR - Average True Range",
    description:
      "The Average True Range (ATR) is a technical analysis oscillator that measures market volatility. It was introduced by market technician J. Welles Wilder Jr. The ATR is the average of true ranges over a specified period, typically based on 14 periods. It takes into account any gaps in the price movement. The ATR does not indicate the price direction but measures volatility, especially volatility caused by price gaps or limit moves. High ATR values usually result from a sharp advance or decline and are unlikely to be sustained for extended periods. A low ATR value indicates a series of periods with small ranges. The ATR is very useful for setting stops or entry triggers, signaling changes in volatility.",
    image1: "/illustrations/converted/indicators/atr.png",
    image1Height: "831px",
    image2: null,
    image2Height: null,
    parameters: [
      {
        key: "dParameter1",
        label: "Period",
        description: undefined,
      },
    ],
    limits: {
      dParameter1: {
        min: 1,
      },
    },
    stringParameters: [],
    defaultValues: {
      nTimeframe: "-2", // Current
      nSeries: "3", // CLOSE
      dParameter1: "14",
    },
    lineIndices: [{ value: "0", label: "Default" }],
  },
  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  2004: {
    id: "2004",
    name: "MACD - Moving Averages Convergence-Divergence",
    description:
      "The Moving Average Convergence-Divergence (MACD) is a trend-following momentum oscillator that shows the relationship between two exponential moving averages (EMAs) of a security's price. The MACD line is calculated by subtracting the 26-period EMA from the 12-period EMA. A nine-day EMA of the MACD line, the signal line, is then plotted on top of the MACD line. Traders may buy the security when the MACD line crosses above the signal line and sell - or short - the security when the MACD line crosses below the signal line. MACD oscillators can be interpreted in several ways, but the more common methods are crossovers, divergences, and rapid rises/falls. It's best used with daily periods, where the traditional setting of 26/12/9 days is the default.",
    image1: "/illustrations/converted/indicators/macd.png",
    image1Height: "831px",
    image2: null,
    image2Height: null,
    parameters: [
      {
        key: "dParameter1",
        label: "Fast EMA period",
        description: undefined,
      },
      {
        key: "dParameter2",
        label: "Slow EMA period",
        description: undefined,
      },
      {
        key: "dParameter3",
        label: "Signal SMA period",
        description: undefined,
      },
    ],
    limits: {
      dParameter1: {
        min: 1,
      },
      dParameter2: {
        min: 1,
      },
      dParameter3: {
        min: 1,
      },
    },
    stringParameters: [],
    defaultValues: {
      nTimeframe: "-2", // Current
      nSeries: "3", // CLOSE
      dParameter1: "12",
      dParameter2: "26",
      dParameter3: "9",
    },
    lineIndices: [
      { value: "0", label: "Main line" },
      { value: "1", label: "Signal line" },
    ],
  },
  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  2005: {
    id: "2005",
    name: "STO - Stochastic",
    description:
      "The Stochastic Oscillator is a momentum oscillator that compares the last closing price with the previous trading range over a particular period. It measures momentum by comparing the closing price to the trading range over a given period. The oscillator is calculated by subtracting the low for the period from the current closing price, dividing by the total range for the period, and multiplying by 100. The Stochastic Oscillator is range-bound, always between 0 and 100. Readings over 80 are considered overbought, and under 20 are considered oversold. However, these do not always indicate impending reversal; powerful trends can maintain overbought or oversold conditions for an extended period. Instead, traders should look to changes in the Stochastic Oscillator for clues about future trend shifts.",
    image1: "/illustrations/converted/indicators/sto.png",
    image1Height: "831px",
    image2: null,
    image2Height: null,
    parameters: [
      {
        key: "dParameter1",
        label: "K line period",
        description: undefined,
      },
      {
        key: "dParameter2",
        label: "D line period",
        description: undefined,
      },
      {
        key: "dParameter3",
        label: "Slowing",
        description: undefined,
      },
      {
        key: "dParameter4",
        label: "Average method",
        description: "• 0 = SMA\n• 1 = EMA\n• 2 = LWMA",
      },
      {
        key: "dParameter5",
        label: "Price field",
        description: "• 0 = Low/High\n• 1 = Close/Close",
      },
    ],
    limits: {
      dParameter1: {
        min: 1,
      },
      dParameter2: {
        min: 1,
      },
      dParameter3: {
        min: 1,
      },
      dParameter4: {
        min: 0,
        max: 2,
      },
      dParameter5: {
        min: 0,
        max: 1,
      },
    },
    stringParameters: [],
    defaultValues: {
      nTimeframe: "-2", // Current
      nSeries: "3", // CLOSE
      dParameter1: "5",
      dParameter2: "3",
      dParameter3: "3",
      dParameter4: "0",
      dParameter5: "0",
    },
    lineIndices: [
      { value: "0", label: "Main line (%K)" },
      { value: "1", label: "Signal line (%D)" },
    ],
  },
  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  2006: {
    id: "2006",
    name: "MOM - Momentum",
    description: (
      <>
        The Momentum Oscillator (MOM) is a technical analysis tool that measures the rate of change in the price of a security over a specific period.
        The oscillator compares the current price of a security to its price at a previous point in time and calculates the difference as a
        percentage. The result is an oscillator that oscillates around 100. Values under 100 indicate negative momentum or decreasing price, and vice
        versa. If the Momentum Oscillator reaches exceptionally high or low values (relative to its historical values), you should assume a
        continuation of the current trend. This makes interpreting an overbought or oversold condition subjective. When the Momentum Oscillator is
        overbought, the security can continue increasing. When the Momentum Oscillator is oversold, the security can also continue to move lower.
        <br />
        <br />
        <b>Note</b>: Momentum line oscillates around 100.
      </>
    ),
    image1: "/illustrations/converted/indicators/mom.png",
    image1Height: "831px",
    image2: null,
    image2Height: null,
    parameters: [
      {
        key: "dParameter1",
        label: "Period",
        description: undefined,
      },
    ],
    limits: {
      dParameter1: {
        min: 1,
      },
    },
    stringParameters: [],
    defaultValues: {
      nTimeframe: "-2", // Current
      nSeries: "3", // CLOSE
      dParameter1: "14",
    },
    lineIndices: [{ value: "0", label: "Default" }],
  },
  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  2007: {
    id: "2007",
    name: "KST - Know Sure Thing",
    description:
      "The Know Sure Thing (KST) is a momentum oscillator developed by Martin Pring to make rate-of-change readings easier for traders to interpret. The KST is calculated by taking the simple moving average (SMA) of four different rate-of-change (ROC) periods, adding them together to come up with the KST, and creating a signal line by taking the 9-period SMA of the KST. Trading signals are generated when the KST crosses the signal line, but traders also look for overbought or oversold conditions. Traders also combine the KST with other technical analyses to maximize their odds of a successful trade. The KST oscillator can be used like many other momentum oscillators, such as the well-known relative strength index (RSI).",
    image1: "/illustrations/converted/indicators/kst.png",
    image1Height: "831px",
    image2: null,
    image2Height: null,
    parameters: [
      {
        key: "dParameter1",
        label: "KST mode",
        description:
          "• 0 = 5-Minute Bar Short-term KST\n• 1 = 5-Minute Bar Long-term KST\n• 2 = 10/15-Minute Bar Short-term KST\n• 3 = 10/15-Minute Bar Long-term KST\n• 4 = Short-term Tick KST\n• 5 = Daily KST Simple Moving Average\n• 6 = Long-term Monthly KST Simple Moving Average\n• 7 = Intermediate KST Simple Moving Average\n• 8 = Intermediate KST Exponential Moving Average\n• 9 = Long-term KST Exponential Moving Average\n• 10 = Short-term KST Weekly Exponential Moving Average",
      },
    ],
    limits: {
      dParameter1: {
        min: 0,
        max: 10,
      },
    },
    stringParameters: [],
    defaultValues: {
      nTimeframe: "-2", // Current
      nSeries: "3", // CLOSE
      dParameter1: "0",
    },
    lineIndices: [{ value: "0", label: "Default" }],
  },
  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  2008: {
    id: "2008",
    name: "OBV - On Balanced Volume",
    description: (
      <>
        The On-Balance Volume (OBV) is a technical trading momentum oscillator that uses volume flow to predict changes in stock price. It's a
        cumulative oscillator, meaning that when the price goes up, that day's volume is added to the cumulative OBV total. If the price decreases,
        that day's volume is subtracted from the OBV total. OBV works best when testing around significant highs and lows to measure for possible
        breakouts and breakdowns. It shows crowd sentiment that can predict a bullish or bearish outcome. Comparing relative action between price bars
        and OBV generates more actionable signals than the green or red volume histograms commonly found at the bottom of price charts.
        <br />
        <br />
        <b>Note</b>: <b>Every tick</b> as model and <b>Close</b> as series recommended. Line is constantly and slowly growing. Thus, it's not a real
        oscillator.
      </>
    ),
    image1: "/illustrations/converted/indicators/obv.png",
    image1Height: "831px",
    image2: null,
    image2Height: null,
    parameters: [],
    stringParameters: [],
    defaultValues: {
      nTimeframe: "-2", // Current
      nSeries: "3", // CLOSE
    },
    lineIndices: [{ value: "0", label: "Default" }],
  },
  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  2009: {
    id: "2009",
    name: "AO - Awesome Oscillator",
    description:
      "The Awesome Oscillator (AO) is a market momentum oscillator developed by Bill Williams. It calculates the difference between a 34-period and a 5-period Simple Moving Average. The averages are not calculated using closing prices but rather each bar's midpoints. AO is generally used to affirm trends or to anticipate possible reversals. The values fluctuate above and below a Zero Line. When AO's values are above the Zero Line, the short-term period trends higher than the long-term period. When AO's values are below the zero line, short-term trends are lower than longer-term ones. This information can be used for a variety of signals.",
    image1: "/illustrations/converted/indicators/ao.png",
    image1Height: "831px",
    image2: null,
    image2Height: null,
    parameters: [],
    stringParameters: [],
    defaultValues: {
      nTimeframe: "-2", // Current
      nSeries: "3", // CLOSE
    },
    lineIndices: [{ value: "0", label: "Default" }],
  },
  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  2010: {
    id: "2010",
    name: "BEP - Bears Power",
    description: (
      <>
        The Bears Power oscillator is a technical analysis tool developed by Alexander Elder. It measures the balance of power between buyers
        ('bulls') and sellers ('bears'). The oscillator is calculated as the difference between the lowest price and a 13-period exponential moving
        average (EMA). When the Bears Power is below zero, sellers are stronger than buyers. If the Bears Power rises above zero, buyers have gained
        strength. This oscillator is often used with a trend oscillator, such as a moving average. It's beneficial for estimating sellers' relative
        strength against buyers and signaling possible trend reversals.
        <br />
        <br />
        <b>Note</b>: <b>Every tick</b> as model and <b>Close</b> as series recommended.
      </>
    ),
    image1: "/illustrations/converted/indicators/bep.png",
    image1Height: "831px",
    image2: null,
    image2Height: null,
    parameters: [
      {
        key: "dParameter1",
        label: "Period",
        description: "dParameter1",
      },
    ],
    limits: {
      dParameter1: {
        min: 1,
      },
    },
    stringParameters: [],
    defaultValues: {
      nTimeframe: "-2", // Current
      nSeries: "3", // CLOSE
      dParameter1: "13",
    },
    lineIndices: [{ value: "0", label: "Default" }],
  },
  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  2011: {
    id: "2011",
    name: "BUP - Bulls Power",
    description: (
      <>
        The Bulls Power oscillator is a technical analysis tool developed by Alexander Elder. It measures the bulls' (buyers') capacity to push prices
        above the average value consensus. The oscillator is calculated as the difference between the highest price of the day and a 13-period
        exponential moving average (EMA). When Bulls Power is above zero, buyers are stronger than sellers. If Bulls Power falls below zero, it
        suggests that sellers have gained strength. This oscillator is often used with a trend oscillator, such as a moving average. It's beneficial
        for estimating buyers' relative strength against sellers and signaling possible trend reversals.
        <br />
        <br />
        <b>Note</b>: <b>Every tick</b> as model and <b>Close</b> as series recommended.
      </>
    ),
    image1: "/illustrations/converted/indicators/bup.png",
    image1Height: "831px",
    image2: null,
    image2Height: null,
    parameters: [
      {
        key: "dParameter1",
        label: "Period",
        description: undefined,
      },
    ],
    limits: {
      dParameter1: {
        min: 1,
      },
    },
    stringParameters: [],
    defaultValues: {
      nTimeframe: "-2", // Current
      nSeries: "3", // CLOSE
      dParameter1: "13",
    },
    lineIndices: [{ value: "0", label: "Default" }],
  },
  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  2012: {
    id: "2012",
    name: "CCI - Commodity Channel Index",
    description:
      "The Commodity Channel Index (CCI) is a momentum-based oscillator developed by Donald Lambert. It measures the difference between the current price and the historical average price. The price is above zero when the CCI is above the historical average. Conversely, the price is below the historic average when the CCI is below zero. The CCI is an unbounded oscillator that can go higher or lower indefinitely. For this reason, overbought and oversold levels are typically determined for each asset by looking at historical extreme CCI levels where the price reversed from. The CCI can be used to identify reversals and divergences in the market. When the CCI crosses above the zero line, it is often considered a bullish signal, and when it crosses below zero, it is considered bearish.",
    image1: "/illustrations/converted/indicators/cci.png",
    image1Height: "831px",
    image2: null,
    image2Height: null,
    parameters: [
      {
        key: "dParameter1",
        label: "Period",
        description: undefined,
      },
    ],
    limits: {
      dParameter1: {
        min: 1,
      },
    },
    stringParameters: [],
    defaultValues: {
      nTimeframe: "-2", // Current
      nSeries: "3", // CLOSE
      dParameter1: "50",
    },
    lineIndices: [{ value: "0", label: "Default" }],
  },
  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  2013: {
    id: "2013",
    name: "DEM - DeMarker",
    description:
      "The DeMarker (DeM) is a technical analysis tool that compares the most recent maximum and minimum prices to the previous period's equivalent price to measure the demand for the underlying asset. From this comparison, it aims to assess the directional trend of the market. It is an oscillator that predicts short-term price action based on intraday high and low changes. The DeMarker oscillator helps traders determine when to enter a market or when to buy or sell an asset to capitalize on probable imminent price trends. It was designed to be a leading oscillator because it attempts to signal an imminent change in price trend. This oscillator is often combined with other signals and is generally used to determine price exhaustion, identify market tops and bottoms, and assess risk levels.",
    image1: "/illustrations/converted/indicators/dem.png",
    image1Height: "831px",
    image2: null,
    image2Height: null,
    parameters: [
      {
        key: "dParameter1",
        label: "Period",
        description: undefined,
      },
    ],
    limits: {
      dParameter1: {
        min: 1,
      },
    },
    stringParameters: [],
    defaultValues: {
      nTimeframe: "-2", // Current
      nSeries: "3", // CLOSE
      dParameter1: "13",
    },
    lineIndices: [{ value: "0", label: "Default" }],
  },
  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  2014: {
    id: "2014",
    name: "FOI - Force Index",
    description:
      "The Force Index is a technical oscillator that measures the amount of power used to move the price of an asset. It was developed by Alexander Elder and is calculated by subtracting yesterday's close from today's close and multiplying the result by today's volume. If closing prices are higher today than yesterday, the force is positive. If closing prices are lower than yesterday's, the force is opposing. The strength of the force is determined either by a larger change in price or a larger volume. The raw value of the force index is plotted as a histogram, with the centerline set to zero. A higher market will result in a positive force index devised above the centerline; a lower market points to an opposing force index below the centerline.",
    image1: "/illustrations/converted/indicators/foi.png",
    image1Height: "831px",
    image2: null,
    image2Height: null,
    parameters: [
      {
        key: "dParameter1",
        label: "Period",
        description: undefined,
      },
      {
        key: "dParameter2",
        label: "Moving average calculation method",
        description: "• 0 = SMA\n• 1 = EMA\n• 2 = LWMA",
      },
    ],
    limits: {
      dParameter1: {
        min: 1,
      },
      dParameter2: {
        min: 0,
        max: 2,
      },
    },
    stringParameters: [],
    defaultValues: {
      nTimeframe: "-2", // Current
      nSeries: "3", // CLOSE
      dParameter1: "13",
      dParameter2: "0",
    },
    lineIndices: [{ value: "0", label: "Default" }],
  },
  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  2015: {
    id: "2015",
    name: "BWM - Market Facilitation Index by Bill Williams",
    description: (
      <>
        The Market Facilitation Index (MFI) is a volatility oscillator developed by Dr. Bill Williams. It aims to determine the willingness of the
        market to move the price. The MFI combines price and volume to analyze the efficiency of price movement. By calculating price movement per
        volume unit, the MFI can help you decide when a trend is strong enough to trade, when a new trend is forming, or when to avoid entering the
        market. It allows you to determine whether the market is liquid and if different types of market players are active.
        <br />
        <br />
        <b>Note</b>: <b>Every tick</b> as model and <b>Close</b> as series recommended.
      </>
    ),
    image1: "/illustrations/converted/indicators/bwm.png",
    image1Height: "831px",
    image2: null,
    image2Height: null,
    parameters: [],
    stringParameters: [],
    defaultValues: {
      nTimeframe: "-2", // Current
      nSeries: "3", // CLOSE
    },
    lineIndices: [
      { value: 0, label: "Value" },
      { value: 1, label: "Color (1=Lime, 2=Brown, 3=Blue, 4=Pink)" },
    ],
  },
  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  2016: {
    id: "2016",
    name: "MFI - Money Flow Index",
    description: (
      <>
        The Money Flow Index (MFI) is a technical oscillator that uses price and volume data to identify overbought or oversold signals in an asset.
        It can also be used to spot divergences that warn of a trend change in price. The oscillator moves between 0 and 100. Unlike conventional
        oscillators such as the Relative Strength Index (RSI), the Money Flow Index incorporates both price and volume data instead of just price. For
        this reason, some analysts call MFI the volume-weighted RSI. An MFI reading above 80 is considered overbought, and an MFI reading below 20 is
        considered oversold, although levels of 90 and 10 are also used as thresholds. A divergence between the oscillator and price is noteworthy.
        <br />
        <br />
        <b>Note</b>: <b>Every tick</b> as model and <b>Typical</b> as series recommended.
      </>
    ),
    image1: "/illustrations/converted/indicators/mfi.png",
    image1Height: "831px",
    image2: null,
    image2Height: null,
    parameters: [
      {
        key: "dParameter1",
        label: "Period",
        description: undefined,
      },
    ],
    limits: {
      dParameter1: {
        min: 1,
      },
    },
    stringParameters: [],
    defaultValues: {
      nTimeframe: "-2", // Current
      nSeries: "5", // TYPICAL
      dParameter1: "14",
    },
    lineIndices: [{ value: "0", label: "Default" }],
  },
  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  2017: {
    id: "2017",
    name: "RVIN - Relative Vigor Index",
    description:
      "The Relative Vigor Index (RVI) is a momentum oscillator that measures the strength of a trend by comparing a security's close price to its trading range. The RVI is based on the concept that prices tend to close higher than they open in uptrends and close lower than in downtrends. It's calculated by subtracting the open from the close and dividing by the total range. The RVI oscillates around a center line rather than a banded trend. Divergences between the RVI oscillator and price suggest a near-term change in the trend. In uptrends, potential buy opportunities occur when the RVI crosses above its signal line. Potential short sale opportunities arise in downtrends when the RVI crosses below its signal line.",
    image1: "/illustrations/converted/indicators/rvin.png",
    image1Height: "831px",
    image2: null,
    image2Height: null,
    parameters: [
      {
        key: "dParameter1",
        label: "Period",
        description: undefined,
      },
    ],
    limits: {
      dParameter1: {
        min: 1,
      },
    },
    stringParameters: [],
    defaultValues: {
      nTimeframe: "-2", // Current
      nSeries: "3", // CLOSE
      dParameter1: "10",
    },
    lineIndices: [
      { value: "0", label: "Main line" },
      { value: "1", label: "Signal line" },
    ],
  },
  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  2018: {
    id: "2018",
    name: "STD - Standard Deviation",
    description:
      "The Standard Deviation is a statistical measure used in trading to quantify the variation or dispersion of a set of values. It measures how much past prices deviate from the mean price in trading. A higher standard deviation means that prices are more volatile, and a lower standard deviation indicates that prices are less volatile. For example, a stock with a standard deviation of $10 has been trading between $80 and $100 over the past year, indicating more volatility than a stock with a standard deviation of $5, trading between $85 and $95 over the same period. Traders use standard deviation to measure volatility, set stop loss levels, and find trading opportunities. It's also used in several trading oscillators to calculate risk vs. return when managing risk.",
    image1: "/illustrations/converted/indicators/std.png",
    image1Height: "831px",
    image2: null,
    image2Height: null,
    parameters: [
      {
        key: "dParameter1",
        label: "Period",
        description: undefined,
      },
      {
        key: "dParameter2",
        label: "Moving average calculation method",
        description: "• 0 = SMA\n• 1 = EMA\n• 2 = LWMA",
      },
    ],
    limits: {
      dParameter1: {
        min: 1,
      },
      dParameter2: {
        min: 0,
        max: 2,
      },
    },
    stringParameters: [],
    defaultValues: {
      nTimeframe: "-2", // Current
      nSeries: "3", // CLOSE
      dParameter1: "10",
      dParameter2: "0",
    },
    lineIndices: [{ value: "0", label: "Default" }],
  },
  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  2019: {
    id: "2019",
    name: "WPR - Williams' Percent Range",
    description:
      "The Williams Percent Range (%R) is a momentum oscillator developed by Larry Williams. It moves between 0 and -100 and measures overbought and oversold levels. The %R compares a stock's closing price to the high-low range over a specific period, typically 14 days or periods. A reading above -20 is considered overbought, while a below -80 is considered oversold. However, an overbought or oversold reading doesn't necessarily mean the price will reverse. Overbought means the price is near the highs of its recent range, and oversold implies the cost is at the lower end of its current range. It can generate trade signals when the price and the oscillator move out of overbought or oversold territory.",
    image1: "/illustrations/converted/indicators/wpr.png",
    image1Height: "831px",
    image2: null,
    image2Height: null,
    parameters: [
      {
        key: "dParameter1",
        label: "Period",
        description: undefined,
      },
    ],
    limits: {
      dParameter1: {
        min: 1,
      },
      dParameter2: {
        min: 0,
        max: 2,
      },
    },
    stringParameters: [],
    defaultValues: {
      nTimeframe: "-2", // Current
      nSeries: "3", // CLOSE
      dParameter1: "14",
    },
    lineIndices: [{ value: "0", label: "Default" }],
  },
  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  2020: {
    id: "2020",
    name: "DOM - Price Dominance",
    description:
      "The Price Dominance Oscillator is a tool that measures the number of bars in the current timeframe where the current price is either the highest or lowest until a new high or low price is established. Consider a scenario where the price is increasing and reaches a local high. The oscillator will count the bars retrospectively until it encounters a local high that is higher. The process is reversed for a decreasing price, which increments the counter until a lower local low is found. The oscillator accumulates the count of these bars, and the larger or smaller the oscillator values, the more likely a significant price correction is imminent. Rapidly increasing or decreasing oscillator values suggest a higher likelihood of a price correction.",
    image1: "/illustrations/converted/indicators/dom.png",
    image1Height: "831px",
    image2: null,
    image2Height: null,
    parameters: [],
    stringParameters: [],
    defaultValues: {
      nTimeframe: "-2", // Current
      nSeries: "3", // CLOSE
    },
    lineIndices: [{ value: "0", label: "Default" }],
  },
  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  2021: {
    id: "2021",
    name: "BST - Bar Strength",
    description: (
      <>
        The algorithm operates by comparing the open price of the current bar with its close price. If the open price is less than the close price,
        the algorithm increments a counter as long as the recent close price remains less than the open price. Conversely, if the open price exceeds
        the close price, the algorithm decrements a counter if the recent close price is more than the open price. The counter's value, whether large
        or small, indicates the number of bars in recent history with either a positive or negative range. Typically, a price correction occurs after
        an extended period of only increasing or decreasing bars. Therefore, the larger the spike in the oscillator, the higher the probability of an
        imminent price correction. This indicator is designed for short-term analysis of the current chart.
        <br />
        <br />
        <b>Note</b>: <b>Every tick</b> as model and <b>Close</b> as series recommended.
      </>
    ),
    image1: "/illustrations/converted/indicators/bst.png",
    image1Height: "831px",
    image2: null,
    image2Height: null,
    parameters: [],
    stringParameters: [],
    defaultValues: {
      nTimeframe: "-2", // Current
      nSeries: "3", // CLOSE
    },
    lineIndices: [{ value: "0", label: "Default" }],
  },
  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  2022: {
    id: "2022",
    name: "ROCC - Cumulative Rate of Change per Bar",
    description: (
      <>
        The algorithm functions by comparing the open price of the current bar to its close price. If the open price is lower than the close price,
        the algorithm computes and accumulates the rate of change per bar, continuing this process as long as the recent close price is less than the
        open price. On the other hand, if the open price is higher than the close price, the algorithm computes and accumulates the negative rate of
        change, continuing this process as long as the recent close price exceeds the open price. The oscillator value's magnitude, whether large or
        small, signifies the significance of the recent price change. This oscillator is beneficial for estimating short-term volatility.
        <br />
        <br />
        <b>Note</b>: <b>Every tick</b> as model and <b>Close</b> as series recommended.
      </>
    ),
    image1: "/illustrations/converted/indicators/rocc.png",
    image1Height: "831px",
    image2: null,
    image2Height: null,
    parameters: [],
    stringParameters: [],
    defaultValues: {
      nTimeframe: "-2", // Current
      nSeries: "3", // CLOSE
    },
    lineIndices: [{ value: "0", label: "Default" }],
  },
  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  2023: {
    id: "2023",
    name: "VOLUME - Volume directly from series",
    description: (
      <>
        The trading framework employed by the Expert Advisor Builder and Custom Expert Advisor maintains a data array with the volume information. You
        can choose the VOLUME oscillator to evaluate this array in the form of an average. This makes it simple to detect events, such as when the
        volume increases or decreases. This oscillator can process either the average of the volume or the ratio between the average and the actual
        volume information. Please note that volume series require a tick-based testing process to represent their values accurately.
        <br />
        <br />
        <b>Note</b>: <b>Every tick</b> as model and <b>Close</b> as series recommended.
      </>
    ),
    image1: "/illustrations/converted/indicators/volume.png",
    image1Height: "831px",
    image2: null,
    image2Height: null,
    parameters: [
      {
        key: "dParameter1",
        label: "Period for generating the volume average",
        description: undefined,
      },
    ],
    limits: {
      dParameter1: {
        min: 1,
      },
    },
    stringParameters: [],
    defaultValues: {
      nTimeframe: "-2", // Current
      nSeries: "3", // CLOSE
      dParameter1: "14",
    },
    lineIndices: [
      { value: "0", label: "Volume" },
      { value: "1", label: "Average volume" },
      { value: "2", label: "Ratio volume to average" },
    ],
  },
  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  2024: {
    id: "2024",
    name: "PPR - Price Profile",
    description: (
      <>
        The price profile oscillator creates a histogram, which can be optionally visualized on the price chart. This histogram segregates the prices
        from a specified period into equal clusters and examines the number of price points within each cluster. The oscillator provides two output
        values for assessing this histogram. The consolidation value line represents the relative size of the current histogram clusters compared to
        the larger one. Conversely, the position value line indicates the placement of the current price within a specific histogram cluster.
        <br />
        <br />
        <b>Note</b>: <b>Every tick</b> as model and <b>Close</b> as series recommended.
      </>
    ),
    image1: "/illustrations/converted/indicators/ppr.png",
    image1Height: "831px",
    image2: null,
    image2Height: null,
    parameters: [
      {
        key: "dParameter1",
        label: "Period",
        description: undefined,
      },
      {
        key: "dParameter2",
        label: "Diagram granulation",
        description: undefined,
      },
      {
        key: "dParameter3",
        label: "Draw flag",
        description: "• 0 = Off\n• 1 = On",
      },
    ],
    limits: {
      dParameter1: {
        min: 1,
      },
      dParameter2: {
        min: 1,
      },
      dParameter3: {
        min: 0,
        max: 1,
      },
    },
    stringParameters: [],
    defaultValues: {
      nTimeframe: "-2", // Current
      nSeries: "3", // CLOSE
      dParameter1: "6000",
      dParameter2: "40",
      dParameter3: "1",
    },
    lineIndices: [
      { value: "0", label: "Consolidation" },
      { value: "1", label: "Position" },
    ],
  },
  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  2025: {
    id: "2025",
    name: "COR - Correlation Coefficient",
    description:
      "In trading, the correlation coefficient measures how two symbols move in relation to each other—ranging from +1 (perfectly aligned) to −1 (opposite directions). Traders use it to manage risk, diversify portfolios, or spot strategic pairs. Strong correlations suggest similar behavior, while weak ones indicate independence, helping refine market decisions with mathematical confidence",
    image1: "/illustrations/converted/indicators/cor.png",
    image1Height: "545px",
    image2: null,
    image2Height: null,
    parameters: [
      {
        key: "dParameter1",
        label: "Period",
        description: undefined,
      },
    ],
    limits: {
      dParameter1: {
        min: 1,
      },
      strParameter1: {
        maxLength: 50,
        emptyAllowed: false,
        disallowedPattern: /[<>{}[\]\\|`~@#$%^&*+=]/,
      },
    },
    stringParameters: [
      {
        key: "strParameter1",
        label: "Symbol for comparison",
        description: "strParameter1",
      },
    ],
    defaultValues: {
      nTimeframe: "-2", // Current
      nSeries: "3", // CLOSE
      dParameter1: "500",
      strParameter1: "Symbol name",
    },
    lineIndices: [{ value: "0", label: "Default" }],
  },
  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  2026: {
    id: "2026",
    name: "CUS - Custom oscillator",
    description:
      "Custom oscillator allows you to use your own external oscillators by specifying the path to the oscillator file. The chart line will be drawn in a seperate window",
    image1: null,
    image1Height: null,
    image2: null,
    image2Height: null,
    parameters: [
      {
        key: "dParameter1",
        label: "Custom parameter 1",
        description: undefined,
      },
      {
        key: "dParameter2",
        label: "Custom parameter 2",
        description: undefined,
      },
      {
        key: "dParameter3",
        label: "Custom parameter 3",
        description: undefined,
      },
      {
        key: "dParameter4",
        label: "Custom parameter 4",
        description: undefined,
      },
      {
        key: "dParameter5",
        label: "Custom parameter 5",
        description: undefined,
      },
      {
        key: "dParameter6",
        label: "Custom parameter 6",
        description: undefined,
      },
      {
        key: "dParameter7",
        label: "Custom parameter 7",
        description: undefined,
      },
      {
        key: "dParameter8",
        label: "Custom parameter 8",
        description: undefined,
      },
      {
        key: "dParameter9",
        label: "Custom parameter 9",
        description: undefined,
      },
      {
        key: "dParameter10",
        label: "Custom parameter 10",
        description: undefined,
      },
      {
        key: "dParameter11",
        label: "Custom parameter 11",
        description: undefined,
      },
      {
        key: "dParameter12",
        label: "Custom parameter 12",
        description: undefined,
      },
      {
        key: "dParameter13",
        label: "Custom parameter 13",
        description: undefined,
      },
      {
        key: "dParameter14",
        label: "Custom parameter 14",
        description: undefined,
      },
      {
        key: "dParameter15",
        label: "Custom parameter 15",
        description: undefined,
      },
      {
        key: "dParameter16",
        label: "Custom parameter 16",
        description: undefined,
      },
      {
        key: "dParameter17",
        label: "Custom parameter 17",
        description: undefined,
      },
      {
        key: "dParameter18",
        label: "Custom parameter 18",
        description: undefined,
      },
      {
        key: "dParameter19",
        label: "Custom parameter 19",
        description: undefined,
      },
      {
        key: "dParameter20",
        label: "Custom parameter 20",
        description: undefined,
      },
      {
        key: "dParameter21",
        label: "Custom parameter 21",
        description: undefined,
      },
      {
        key: "dParameter22",
        label: "Custom parameter 22",
        description: undefined,
      },
      {
        key: "dParameter23",
        label: "Custom parameter 23",
        description: undefined,
      },
      {
        key: "dParameter24",
        label: "Custom parameter 24",
        description: undefined,
      },
      {
        key: "dParameter25",
        label: "Custom parameter 25",
        description: undefined,
      },
      {
        key: "dParameter26",
        label: "Custom parameter 26",
        description: undefined,
      },
      {
        key: "dParameter27",
        label: "Custom parameter 27",
        description: undefined,
      },
      {
        key: "dParameter28",
        label: "Custom parameter 28",
        description: undefined,
      },
      {
        key: "dParameter29",
        label: "Custom parameter 29",
        description: undefined,
      },
      {
        key: "dParameter30",
        label: "Custom parameter 30",
        description: undefined,
      },
      {
        key: "dParameter31",
        label: "Custom parameter 31",
        description: undefined,
      },
      {
        key: "dParameter32",
        label: "Custom parameter 32",
        description: undefined,
      },
    ],
    limits: {
      dParameter1: {
        digits: 8,
      },
      dParameter2: {
        digits: 8,
      },
      dParameter3: {
        digits: 8,
      },
      dParameter4: {
        digits: 8,
      },
      dParameter5: {
        digits: 8,
      },
      dParameter6: {
        digits: 8,
      },
      dParameter7: {
        digits: 8,
      },
      dParameter8: {
        digits: 8,
      },
      dParameter9: {
        digits: 8,
      },
      dParameter10: {
        digits: 8,
      },
      dParameter11: {
        digits: 8,
      },
      dParameter12: {
        digits: 8,
      },
      dParameter13: {
        digits: 8,
      },
      dParameter14: {
        digits: 8,
      },
      dParameter15: {
        digits: 8,
      },
      dParameter16: {
        digits: 8,
      },
      dParameter17: {
        digits: 8,
      },
      dParameter18: {
        digits: 8,
      },
      dParameter19: {
        digits: 8,
      },
      dParameter20: {
        digits: 8,
      },
      dParameter21: {
        digits: 8,
      },
      dParameter22: {
        digits: 8,
      },
      dParameter23: {
        digits: 8,
      },
      dParameter24: {
        digits: 8,
      },
      dParameter25: {
        digits: 8,
      },
      dParameter26: {
        digits: 8,
      },
      dParameter27: {
        digits: 8,
      },
      dParameter28: {
        digits: 8,
      },
      dParameter29: {
        digits: 8,
      },
      dParameter30: {
        digits: 8,
      },
      dParameter31: {
        digits: 8,
      },
      dParameter32: {
        digits: 8,
      },
    },
    stringParameters: [
      {
        key: "strParameter1",
        label: "String custom parameter 1",
        description: undefined,
      },
      {
        key: "strParameter2",
        label: "String custom parameter 2",
        description: undefined,
      },
      {
        key: "strParameter3",
        label: "String custom parameter 3",
        description: undefined,
      },
      {
        key: "strParameter4",
        label: "String custom parameter 4",
        description: undefined,
      },
      {
        key: "strMt4CustomPath",
        label: "MetaTrader4 path to indicator file",
        description: 'Relative path starting from the "Indicators" folder in "Data Folder > MQL4".',
      },
      {
        key: "strMt5CustomPath",
        label: "MetaTrader5 path to indicator file",
        description: 'Relative path starting from the "Indicators" folder in "Data Folder > MQL5".',
      },
    ],
    defaultValues: {
      nTimeframe: "-2", // Current
      nSeries: "3", // CLOSE
      dParameter1: "0.00000000",
      dParameter2: "0.00000000",
      dParameter3: "0.00000000",
      dParameter4: "0.00000000",
      dParameter5: "0.00000000",
      dParameter6: "0.00000000",
      dParameter7: "0.00000000",
      dParameter8: "0.00000000",
      dParameter9: "0.00000000",
      dParameter10: "0.00000000",
      dParameter11: "0.00000000",
      dParameter12: "0.00000000",
      dParameter13: "0.00000000",
      dParameter14: "0.00000000",
      dParameter15: "0.00000000",
      dParameter16: "0.00000000",
      dParameter17: "0.00000000",
      dParameter18: "0.00000000",
      dParameter19: "0.00000000",
      dParameter20: "0.00000000",
      dParameter21: "0.00000000",
      dParameter22: "0.00000000",
      dParameter23: "0.00000000",
      dParameter24: "0.00000000",
      dParameter25: "0.00000000",
      dParameter26: "0.00000000",
      dParameter27: "0.00000000",
      dParameter28: "0.00000000",
      dParameter29: "0.00000000",
      dParameter30: "0.00000000",
      dParameter31: "0.00000000",
      dParameter32: "0.00000000",
      strMt4CustomPath: "\\Folder\\MyExternalOscillator.ex4",
      strMt5CustomPath: "Folder\\MyExternalOscillator",
    },
    lineIndices: [],
  },
  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  2027: {
    id: "2027",
    name: "ORAFI - Range finder (separate window)",
    description: <>
      The Range Finder is an advanced technical analysis indicator that automatically identifies and tracks optimal trading ranges by analyzing price action patterns and market structure. It employs a sophisticated ZigZag algorithm with ATR-based swing filtering to detect significant price pivots, then evaluates multiple range configurations to find the most statistically robust trading boundaries.
      <br />
      <br />
      The indicator calculates upper and lower range limits with customizable buffer zones and breakout thresholds, dynamically adapting to market conditions through a weighted scoring system that considers touch point distribution, price excursion balance, and range age. Key features include Gini coefficient analysis for touch point equality, median swing height calculations using Fibonacci multipliers, and automatic range updates based on breakout events or statistical deterioration. 
      <br />
      <br />
      The Range Finder provides traders with clear visual boundaries for range-bound trading strategies, offering both current price position within the range (0-100%) and discrete range slots for systematic entry and exit decisions. It's particularly effective in consolidating markets where price oscillates between well-defined support and resistance levels, helping traders identify optimal entry points near range boundaries and potential breakout opportunities when price moves beyond established thresholds.
      <br />
      <br />
      This tool is available in two formats: as an oscillator and as a standard indicator. The oscillator version displays the selected line in a separate window, typically below the main chart. The indicator version renders the line directly within the main chart window, overlaying price data. 
      <br />
      <br />
      The following lines are available, along with their recommended display windows:
      <ul style={{ listStyleType: "disc" }}>
        <li style={{ marginBottom: "10px" }}>
          <b>Current price relative to range:</b> Separate window
        </li>
        <li style={{ marginBottom: "10px" }}>
          <b>Current range slot:</b> Separate window
        </li>
        <li style={{ marginBottom: "10px" }}>
          <b>Upper breakout price:</b> Same window
        </li>
        <li style={{ marginBottom: "10px" }}>
          <b>Upper price:</b> Same window
        </li>
        <li style={{ marginBottom: "10px" }}>
          <b>Upper buffer price:</b> Same window
        </li>
        <li style={{ marginBottom: "10px" }}>
          <b>Lower buffer price:</b> Same window
        </li>
        <li style={{ marginBottom: "10px" }}>
          <b>Lower price:</b> Same window
        </li>
        <li style={{ marginBottom: "10px" }}>
          <b>Lower breakout price:</b> Same window
        </li>
      </ul>    
    </>,
    image1: "/illustrations/converted/indicators/rafi_1.png",
    image1Height: "663px",
    image2: "/illustrations/converted/indicators/rafi_2.png",
    image2Height: "663px",
    parameters: [
      {
        key: "dParameter1",
        label: "Minimum range length",
        description: undefined,
      },
      {
        key: "dParameter2",
        label: "Maximum range length",
        description: undefined,
      },
      {
        key: "dParameter3",
        label: "Step size for range testing",
        description: undefined,
      },
      {
        key: "dParameter4",
        label: "Minimum range height ",
        description: "Multiplies with ZigZag median swing height.",
      },
      {
        key: "dParameter5",
        label: "Maximum range height",
        description: "Multiplies with ZigZag median swing height.",
      },
      {
        key: "dParameter6",
        label: "Buffer height",
        description: "<b>>0</b>: In percent of range.\n<b><0</b>: Fibonacci level * ZigZag median swing height\n\nFibonacci levels:\n<b>-0</b> = 5.6%\n<b>-1</b> = 9.0%\n<b>-2</b> = 14.6%\n<b>-3</b> = 23.6%\n<b>-4</b> = 38.2%\n<b>-5</b> = 50.0%\n<b>-6</b> = 61.8%\n<b>-7</b> = 78.6%\n<b>-8</b> = 88.6%\n<b>-9</b> = 100.0%\n<b>-10</b> = 161.8%\n<b>-11</b> = 261.8%\n<b>-12</b> = 423.6%\n<b>-13</b> = 685.4%",
      },
      {
        key: "dParameter7",
        label: "Breakout height",
        description: "<b>>0</b>: In percent of range.\n<b><0</b>: Fibonacci level * ZigZag median swing height\n\nFibonacci levels:\n<b>-0</b> = 5.6%\n<b>-1</b> = 9.0%\n<b>-2</b> = 14.6%\n<b>-3</b> = 23.6%\n<b>-4</b> = 38.2%\n<b>-5</b> = 50.0%\n<b>-6</b> = 61.8%\n<b>-7</b> = 78.6%\n<b>-8</b> = 88.6%\n<b>-9</b> = 100.0%\n<b>-10</b> = 161.8%\n<b>-11</b> = 261.8%\n<b>-12</b> = 423.6%\n<b>-13</b> = 685.4%",
      },
      {
        key: "dParameter8",
        label: "Minimum holding bars",
        description: "Specifies the minimum duration a range remains fixed before initiating a search for a new one.",
      },
      {
        key: "dParameter9",
        label: "Draw ranges",
        description: "Activates or deactivates the built-in drawing tool for defined ranges.",
      },
    ],
    limits: {
      dParameter1: {
        digits: 0,
        min: 0,
      },
      dParameter2: {
        digits: 0,
        min: 0,
      },
      dParameter3: {
        digits: 0,
        min: 0,
      },
      dParameter4: {
        digits: 1,
        step: 0.1,
        min: 1.0,
      },
      dParameter5: {
        digits: 1,
        step: 0.1,
        min: 1.0,
      },
      dParameter6: {
        digits: 0,
      },
      dParameter7: {
        digits: 0,
      },
      dParameter8: {
        digits: 0,
        min: 0,
      },
      dParameter9: {
        digits: 0,
        min: 0,
        max: 1,
      },
    },
    stringParameters: [],
    defaultValues: {
      nTimeframe: "-2", // Current
      nSeries: "0", // OPEN
      dParameter1: "50",
      dParameter2: "300",
      dParameter3: "25",
      dParameter4: "3.5",
      dParameter5: "6.0",
      dParameter6: "-9",
      dParameter7: "-10",
      dParameter8: "50",
      dParameter9: "1",
    },
    lineIndices: [
      { value: "0", label: "Current price relative to range" },
      { value: "1", label: "Current range slot" },
      { value: "2", label: "Upper breakout price" },
      { value: "3", label: "Upper price" },
      { value: "4", label: "Upper buffer price" },
      { value: "5", label: "Lower buffer price" },
      { value: "6", label: "Lower price" },
      { value: "7", label: "Lower breakout price" },
    ],
  },
  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  2028: {
    id: "2028",
    name: "OZZFI - ZigZag Fibonacci (separate window)",
    description: <>
      The ZigZag indicator is a dynamic technical analysis tool that identifies significant price swings by filtering out minor price movements and market noise. It uses an ATR-based adaptive algorithm to determine minimum swing thresholds, ensuring the indicator automatically adjusts to different market volatilities and timeframes. The indicator employs a three-pass algorithm: first identifying local extremes using depth parameters, then removing closely spaced extremes through backstep filtering, and finally building an alternating sequence of highs and lows that meet minimum swing size requirements. 
      <br />
      <br />
      Key features include automatic Fibonacci retracement level calculations based on recent swings, with support for extended Fibonacci ratios from 5.6% to 423.6%. The indicator calculates median swing heights across all detected pivots, providing a statistical measure of typical market movement ranges. Visual components include color-coded swing lines (green for upswings, red for downswings) and optional Fibonacci level overlays with percentage labels. 
      <br />
      <br />
      The ZigZag is particularly valuable for identifying trend reversals, measuring retracement levels, and determining optimal entry and exit points based on swing structure. It helps traders visualize the underlying market rhythm by connecting significant peaks and troughs while ignoring insignificant fluctuations, making it ideal for swing trading strategies and Elliott Wave analysis.
      <br />
      <br />
      This tool is available in two formats: as an oscillator and as a standard indicator. The oscillator version displays the selected line in a separate window, typically below the main chart. The indicator version renders the line directly within the main chart window, overlaying price data. 
      <br />
      <br />
      The following lines are available, along with their recommended display windows:
      <ul style={{ listStyleType: "disc" }}>
        <li style={{ marginBottom: "10px" }}>
          <b>ZigZag median swing height:</b> Separate window
        </li>
        <li style={{ marginBottom: "10px" }}>
          <b>Fibonacci level value:</b> Same window
        </li>
      </ul>    
    </>,
    image1: "/illustrations/converted/indicators/zzfi_1.png",
    image1Height: "663px",
    image2: "/illustrations/converted/indicators/zzfi_2.png",
    image2Height: "663px",
    parameters: [
      {
        key: "dParameter1",
        label: "Period",
        description: "Period for ZigZag analysis (bars to analyze)",
      },
      {
        key: "dParameter2",
        label: "Sensitivity",
        description: "Smaller depth for more sensitivity",
      },
      {
        key: "dParameter3",
        label: "Minimum swing height",
        description: "Multiplier to increase/decrease minimum swing requirement",
      },
      {
        key: "dParameter4",
        label: "Backstep height",
        description: "Less backstep for more ZigZag points.",
      },
      {
        key: "dParameter5",
        label: "Swing for Fibonacci feature",
        description: "ZigZag swing from which the Fibonacci retracement begins.",
      },
      {
        key: "dParameter6",
        label: "Fibonacci level",
        description: "Fibonacci level value to return.\n\nFibonacci levels:\n<b>0</b> = 5.6%\n<b>1</b> = 9.0%\n<b>2</b> = 14.6%\n<b>3</b> = 23.6%\n<b>4</b> = 38.2%\n<b>5</b> = 50.0%\n<b>6</b> = 61.8%\n<b>7</b> = 78.6%\n<b>8</b> = 88.6%\n<b>9</b> = 100.0%\n<b>10</b> = 161.8%\n<b>11</b> = 261.8%\n<b>12</b> = 423.6%\n<b>13</b> = 685.4%",
      },
      {
        key: "dParameter7",
        label: "Draw flag",
        description: "Activates or deactivates the built-in drawing tool.\n\nOptions are:\n<b>0</b> = Nothing drawn\n<b>1</b> = Fibonacci retracements drawn\n<b>2</b> = ZigZag drawn\n<b>3</b> = Fibonacci retracements and ZigZag drawn",
      },
    ],
    limits: {
      dParameter1: {
        digits: 0,
        min: 0,
      },
      dParameter2: {
        digits: 0,
        min: 0,
      },
      dParameter3: {
        digits: 1,
        step: 0.1,
        min: 1.0,
      },
      dParameter4: {
        digits: 0,
        min: 0,
      },
      dParameter5: {
        digits: 0,
        min: 0,
      },
      dParameter6: {
        digits: 0,
        min: 0,
      },
      dParameter7: {
        digits: 0,
        min: 0,
        max: 3,
      },
    },
    stringParameters: [],
    defaultValues: {
      nTimeframe: "-2", // Current
      nSeries: "3", // Close
      dParameter1: "3000",
      dParameter2: "6",
      dParameter3: "5.0",
      dParameter4: "4",
      dParameter5: "1",
      dParameter6: "10",
      dParameter7: "3",
    },
    lineIndices: [
      { value: "0", label: "ZigZag median swing height" },
      { value: "1", label: "Fibonacci level value" },
      { value: "2", label: "Direction of selected swing" },
    ],
  },
};

// ----------------------------------------------------------------------
// Section
// ----------------------------------------------------------------------
export const ALL_CONFIGS = { ...INDICATOR_CONFIGS, ...OSCILLATOR_CONFIGS };

// ----------------------------------------------------------------------
// Section
// ----------------------------------------------------------------------
const SERIES_OPTIONS = [
  { value: "0", label: "Open" },
  { value: "1", label: "High" },
  { value: "2", label: "Low" },
  { value: "3", label: "Close" },
  { value: "4", label: "Median (HL/2)" },
  { value: "5", label: "Typical (HLC/3)" },
  { value: "6", label: "Weighted (HLCC/4)" },
  { value: "7", label: "Volume" },
  { value: "8", label: "Time" },
  { value: "9", label: "Body" },
  { value: "10", label: "Range" },
  { value: "11", label: "Shadow top" },
  { value: "12", label: "Shadow bottom" },
];

// ----------------------------------------------------------------------
// Section
// ----------------------------------------------------------------------
const TIMEFRAME_OPTIONS = [
  { value: "-2", label: "Current" },
  { value: "0", label: "M1" },
  { value: "1", label: "M5" },
  { value: "2", label: "M15" },
  { value: "3", label: "M30" },
  { value: "4", label: "H1" },
  { value: "5", label: "H4" },
  { value: "6", label: "D1" },
  { value: "7", label: "W1" },
  { value: "8", label: "MN" },
];

// ----------------------------------------------------------------------
// Section
// ----------------------------------------------------------------------
const USED_FOR_OPTIONS = [
  { value: "0", label: "Entry" },
  { value: "1", label: "Exit" },
  { value: "2", label: "Forward" },
];

// ----------------------------------------------------------------------
// Section
// ----------------------------------------------------------------------
const CONDITION_OPTIONS = [
  { value: "0", label: "Off" },
  { value: "1", label: "Smaller limit 1" },
  { value: "2", label: "Greater limit 1" },
  { value: "3", label: "Equals limit 1" },
  { value: "4", label: "Between limit 1 and limit 2" },
  { value: "5", label: "Outside limit 1 and limit 2" },
  { value: "6", label: "Cross limit 1" },
  { value: "7", label: "Limit 1 crossing" },
  { value: "8", label: "Cross any" },
];

// ----------------------------------------------------------------------
// Section
// ----------------------------------------------------------------------
export const getIndicatorDisplayName = (selectionId) => {
  const config = ALL_CONFIGS[selectionId];
  return config ? config.name : `Indicator ${selectionId}`;
};

// ----------------------------------------------------------------------
// Section
// ----------------------------------------------------------------------
export const getIndicatorShortName = (selectionId) => {
  const config = ALL_CONFIGS[selectionId];
  if (!config) {
    return `IND${selectionId}`;
  }

  const shortName = config.name.split(" - ")[0];
  return shortName;
};

// ----------------------------------------------------------------------
// Section
// ----------------------------------------------------------------------
export const getLineIndexName = (indicatorId, lineIndex) => {
  // Get the indicator configuration
  const config = ALL_CONFIGS[indicatorId];

  // Return default if config doesn't exist
  if (!config) {
    return `Line ${lineIndex}`;
  }

  // Return default if lineIndices doesn't exist
  if (!config.lineIndices || !Array.isArray(config.lineIndices)) {
    return `Line ${lineIndex}`;
  }

  // Find the line with matching value
  const lineItem = config.lineIndices.find((line) => line.value === lineIndex);

  // Return the label if found, otherwise return a default
  return lineItem ? lineItem.label : `Line ${lineIndex}`;
};

// ----------------------------------------------------------------------
// Section
// ----------------------------------------------------------------------
export const getLineIndexNameFromConfig = (config, lineIndex) => {
  // Return default if config doesn't exist
  if (!config) {
    return `Line ${lineIndex}`;
  }

  // Return default if lineIndices doesn't exist
  if (!config.lineIndices || !Array.isArray(config.lineIndices)) {
    return `Line ${lineIndex}`;
  }

  // Find the line with matching value
  const lineItem = config.lineIndices.find((line) => line.value === lineIndex);

  // Return the label if found, otherwise return a default
  return lineItem ? lineItem.label : `Line ${lineIndex}`;
};

// ----------------------------------------------------------------------
// Section
// ----------------------------------------------------------------------
export const getActiveParameters = (config) => {
  const params = [];

  // Get the indicator/oscillator configuration
  const indicatorConfig = ALL_CONFIGS[config.nSelection];

  if (!indicatorConfig) {
    return params;
  }

  // Only check parameters that are defined in the indicator configuration
  if (indicatorConfig.parameters && indicatorConfig.parameters.length > 0) {
    indicatorConfig.parameters.forEach((paramDef) => {
      const paramValue = config[paramDef.key];

      // Only include if the parameter is defined in the config and has a value
      if (paramValue !== undefined) {
        params.push(paramValue);
      }
    });
  }

  return params;
};

// ----------------------------------------------------------------------
// Section
// ----------------------------------------------------------------------
export const getDetailedParameters = (config) => {
  const params = [];

  // Get the indicator configuration to access parameter metadata
  const indicatorConfig = ALL_CONFIGS[config.nSelection];

  if (!indicatorConfig) {
    return params;
  }

  // Only show numeric parameters that are defined in the indicator configuration
  if (indicatorConfig.parameters && indicatorConfig.parameters.length > 0) {
    indicatorConfig.parameters.forEach((paramDef) => {
      const paramValue = config[paramDef.key];

      // Only include if the parameter is defined in the config and has a value
      if (paramValue !== undefined) {
        params.push({
          type: "numeric",
          key: paramDef.key,
          label: paramDef.label,
          value: paramValue,
          description: paramDef.description,
        });
      }
    });
  }

  // Show string parameters that are defined in the indicator configuration
  if (indicatorConfig.stringParameters && indicatorConfig.stringParameters.length > 0) {
    indicatorConfig.stringParameters.forEach((paramDef) => {
      const paramValue = config[paramDef.key];

      // Include if the parameter is defined in the indicator config
      // Show empty values too, but exclude PATH parameters unless they have values
      const isPathParameter = paramDef.key === "strMt4CustomPath" || paramDef.key === "strMt5CustomPath";

      if (paramValue !== undefined) {
        // For PATH parameters, only show if they have non-empty values
        if (isPathParameter) {
          if (paramValue && paramValue.trim() !== "") {
            params.push({
              type: "string",
              key: paramDef.key,
              label: paramDef.label,
              value: paramValue,
              description: paramDef.description,
            });
          }
        } else {
          // For non-PATH string parameters, show even if empty
          params.push({
            type: "string",
            key: paramDef.key,
            label: paramDef.label,
            value: paramValue,
            description: paramDef.description,
          });
        }
      }
    });
  }

  return params;
};

// ----------------------------------------------------------------------
// Section
// ----------------------------------------------------------------------
export const IndicatorsBuildName = (config) => {
  const parts = [];

  parts.push(`${config.nSignalInUse ? "On" : "Off"}`);

  parts.push(`${getUsedForDisplay(config.nSignalUsedFor)}`);

  if (config.nLineIndex !== undefined && getLineIndexName(config.nSelection, config.nLineIndex) !== "Default") {
    parts.push(`${getLineIndexName(config.nSelection, config.nLineIndex)}`);
  }

  const activeParams = getActiveParameters(config);
  if (activeParams.length > 0) {
    parts.push(activeParams.join(", "));
  }

  let joinedParts = parts.join("|");
  if (joinedParts.length > 30) {
    joinedParts = joinedParts.substring(0, 27) + "...";
  }

  return `${getIndicatorShortName(config.nSelection)} (${joinedParts})`;
};

// ----------------------------------------------------------------------
// Section
// ----------------------------------------------------------------------
export const getSeriesDisplay = (series) => {
  const option = SERIES_OPTIONS.find((opt) => opt.value === series);
  return option ? option.label : series;
};

// ----------------------------------------------------------------------
// Section
// ----------------------------------------------------------------------
export const getTimeframeDisplay = (timeframe) => {
  const option = TIMEFRAME_OPTIONS.find((opt) => opt.value === timeframe);
  return option ? option.label : timeframe;
};

// ----------------------------------------------------------------------
// Section
// ----------------------------------------------------------------------
export const getUsedForDisplay = (usedFor) => {
  const option = USED_FOR_OPTIONS.find((opt) => opt.value === usedFor);
  return option ? option.label : usedFor;
};

// ----------------------------------------------------------------------
// Section
// ----------------------------------------------------------------------
export const getConditionDisplay = (condition) => {
  const option = CONDITION_OPTIONS.find((opt) => opt.value === condition);
  return option ? option.label : condition;
};

// ----------------------------------------------------------------------
// Section
// ----------------------------------------------------------------------
export const IndicatorsBuildSubtitle = (config) =>
  `${config.strSymbol} | ${getTimeframeDisplay(config.nTimeframe)} | ${getSeriesDisplay(config.nSeries)} | ${config.nShift}`;

// ----------------------------------------------------------------------
// Section
// ----------------------------------------------------------------------
const conditionRequiresLimit1 = (condition) =>
  // Conditions that use Limit 1: 1,2,3,4,5,6,7 (all except 0=Off and 8=Cross any)
  parseInt(condition, 10) >= 1 && parseInt(condition, 10) <= 8;
// ----------------------------------------------------------------------
// Section
// ----------------------------------------------------------------------
const conditionRequiresLimit2 = (condition) =>
  // Conditions that use Limit 2: 4,5 (Between and Outside)
  parseInt(condition, 10) === 4 || parseInt(condition, 10) === 5;
// ----------------------------------------------------------------------
// Section
// ----------------------------------------------------------------------
export function IndicatorsRenderParameters(selectedIndicator, getIndicatorConfig, professionalMode) {
  const config = getIndicatorConfig(selectedIndicator);

  if (!config) {
    return (
      <Typography variant="contentMiddle" align="center">
        No configuration saved yet!
      </Typography>
    );
  }

  const renderParam = (label, value, leftWidth = "auto", rightWidth = "full") => (
    <Box sx={{ display: "flex", width: "100%", gap: 1 }}>
      <Typography
        variant="contentTiny"
        sx={{
          fontWeight: 600,
          flex: `1 1 ${leftWidth}`,
          maxWidth: leftWidth,
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="contentTiny"
        sx={{
          flex: `1 1 ${rightWidth}`,
          maxWidth: rightWidth,
          wordBreak: "break-word",
          textAlign: "right",
        }}
      >
        {value}
      </Typography>
    </Box>
  );

  const detailedParams = getDetailedParameters(config);

  return (
    <Stack spacing={0.5}>
      {renderParam("Signal in use", config.nSignalInUse ? "On" : "Off")}
      {renderParam("Signal used for", getUsedForDisplay(config.nSignalUsedFor))}
      <Box>
        <Divider sx={{ mt: 0, mb: 0, borderColor: "#C8C8C8" }} />
      </Box>
      {renderParam("Selected indicator", getIndicatorDisplayName(config.nSelection))}

      {config.nLineIndex !== undefined && getLineIndexName(config.nSelection, config.nLineIndex) !== "Default"
        ? renderParam("Used line", getLineIndexName(config.nSelection, config.nLineIndex))
        : null}

      {detailedParams.map((param, index) => (
        <Box key={param.key || `param-${index}`}>{renderParam(param.label, param.value)}</Box>
      ))}
      {renderParam("Symbol", config.strSymbol)}
      {renderParam("Timeframe", getTimeframeDisplay(config.nTimeframe))}
      {renderParam("Series", getSeriesDisplay(config.nSeries))}
      {renderParam("Shift", config.nShift)}

      <Box>
        <Divider sx={{ mt: 0, mb: 0, borderColor: "#C8C8C8" }} />
      </Box>
      {renderParam("Value condition", getConditionDisplay(config.nValueCondition))}
      {/* Only show value limits if the condition requires them */}
      {conditionRequiresLimit1(config.nValueCondition) && renderParam("Value limit 1", config.dValueLimit1)}
      {conditionRequiresLimit2(config.nValueCondition) && renderParam("Value limit 2", config.dValueLimit2)}

      <Box>
        <Divider sx={{ mt: 0, mb: 0, borderColor: "#C8C8C8" }} />
      </Box>

      {renderParam("Gradient condition", getConditionDisplay(config.nAngleCondition))}
      {/* Only show gradient limits if the condition requires them */}
      {conditionRequiresLimit1(config.nAngleCondition) && renderParam("Gradient limit 1", config.dAngleLimit1)}
      {conditionRequiresLimit2(config.nAngleCondition) && renderParam("Gradient limit 2", config.dAngleLimit2)}

      <Box>
        <Divider sx={{ mt: 0, mb: 0, borderColor: "#C8C8C8" }} />
      </Box>
      {renderParam("Signal visualization", config.nSignalVisualization ? "On" : "Off")}

      {professionalMode === true && renderParam("Draw in chart", config.nDrawInChart ? "On" : "Off")}

      {/* Only show drawing-related fields when Draw in chart is enabled */}
      {professionalMode === true && config.nDrawInChart === true && renderParam("Log drawing slot", config.nLogDrawingSlot ? "On" : "Off")}

      {professionalMode === true && config.nDrawInChart === true && renderParam("Drawing slot", config.nDrawingSlot)}

      {professionalMode === true ? (
        <Box>
          <Divider sx={{ mt: 0, mb: 0, borderColor: "#C8C8C8" }} />
        </Box>
      ) : null}

      {professionalMode === true ? <>{renderParam("Exclusion time range", config.strExclusion)}</> : null}

      {professionalMode === true ? <>{renderParam("Value analysis feedback state", config.nValueAnalysisFeedbackState ? "On" : "Off")}</> : null}

      {professionalMode === true ? <>{renderParam("Signal analysis feedback state", config.nSignalAnalysisFeedbackState ? "On" : "Off")}</> : null}

      {professionalMode === true ? <>{renderParam("Forward adjustment", config.dForwardAdjustment)}</> : null}
    </Stack>
  );
}

// ----------------------------------------------------------------------
// Section
// ----------------------------------------------------------------------
const INDICATOR_OPTIONS = Object.values(ALL_CONFIGS)
  .map((config) => ({
    value: config.id,
    label: config.name,
  }))
  .sort((a, b) => a.label.localeCompare(b.label));

// ----------------------------------------------------------------------
// Main element export(s)
// ----------------------------------------------------------------------
export const IndicatorsConfigure = forwardRef(({ onSave, initialData, professionalMode, indicators }, ref) => {
  // ----------------------------------------------------------------------
  // Properties
  // ----------------------------------------------------------------------
  IndicatorsConfigure.propTypes = {
    onSave: PropTypes.func.isRequired,
    initialData: PropTypes.object,
    professionalMode: PropTypes.any,
    indicators: PropTypes.any,
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const [scroll, setScroll] = useState("paper");
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);
  const autoCloseTimeoutRef = useRef(null);

  const [error, setError] = useState(false);
  const [success, setSuccess] = useState(false);
  const [warning, setWarning] = useState(false);
  const [returnMessage, setReturnMessage] = useState("");

  const [selectedConfig, setSelectedConfig] = useState(null);

  const [collapseIndicator, setCollapseIndicator] = useState(false);
  const [collapseValue, setCollapseValue] = useState(false);
  const [collapseGradient, setCollapseGradient] = useState(false);

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  useImperativeHandle(ref, () => ({
    openDialog: () => {
      setOpen(true);
    },
    closeDialog: () => {
      setOpen(false);
    },
  }));

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const handleReset = () => {
    // Get the currently selected indicator
    const currentSelection = values.nSelection;

    if (!currentSelection || !ALL_CONFIGS[currentSelection]) {
      console.warn("No indicator selected or invalid selection");
      return;
    }

    const config = ALL_CONFIGS[currentSelection];

    // Start with global defaults for all fields
    const allDefaults = {
      // Global defaults from IndicatorDefaults
      nSignalInUse: true,
      nSignalUsedFor: "0",
      nSignalVisualization: false,
      nDrawInChart: true,
      nLogDrawingSlot: false,
      nDrawingSlot: getNextAvailableDrawingSlot(indicators),
      nSelection: currentSelection, // Keep the current selection
      strSymbol: "Current",
      nTimeframe: config.defaultValues.nTimeframe || "-2",
      nSeries: config.defaultValues.nSeries || "0",
      nShift: "0",
      nLineIndex: "0",
      // Reset all 32 numeric parameters to "0" first
      dParameter1: "0",
      dParameter2: "0",
      dParameter3: "0",
      dParameter4: "0",
      dParameter5: "0",
      dParameter6: "0",
      dParameter7: "0",
      dParameter8: "0",
      dParameter9: "0",
      dParameter10: "0",
      dParameter11: "0",
      dParameter12: "0",
      dParameter13: "0",
      dParameter14: "0",
      dParameter15: "0",
      dParameter16: "0",
      dParameter17: "0",
      dParameter18: "0",
      dParameter19: "0",
      dParameter20: "0",
      dParameter21: "0",
      dParameter22: "0",
      dParameter23: "0",
      dParameter24: "0",
      dParameter25: "0",
      dParameter26: "0",
      dParameter27: "0",
      dParameter28: "0",
      dParameter29: "0",
      dParameter30: "0",
      dParameter31: "0",
      dParameter32: "0",
      // Reset all string parameters
      strParameter1: "",
      strParameter2: "",
      strParameter3: "",
      strParameter4: "",
      strMt4CustomPath: "",
      strMt5CustomPath: "",
      // Reset condition fields
      nAngleCondition: "0",
      dAngleLimit1: "0",
      dAngleLimit2: "0",
      nValueCondition: "0",
      dValueLimit1: "0",
      dValueLimit2: "0",
      // Other fields
      dForwardAdjustment: "1.00",
      nValueAnalysisFeedbackState: true,
      nSignalAnalysisFeedbackState: true,
      strExclusion: "00:00:00-00:00:00",
    };

    // Override with indicator-specific parameter defaults
    if (config.parameters) {
      config.parameters.forEach((param) => {
        allDefaults[param.key] = config.defaultValues[param.key] || "0";
      });
    }

    // Override with indicator-specific string parameter defaults
    if (config.stringParameters) {
      config.stringParameters.forEach((param) => {
        allDefaults[param.key] = config.defaultValues[param.key] || "";
      });
    }

    // Clear all errors and touched state
    setErrors({});
    setTouched({});
    // Clear cross-validation error state
    setError(false);
    setReturnMessage("");

    // Apply all the default values to the form
    Object.keys(allDefaults).forEach((key) => {
      setFieldValue(key, allDefaults[key]);
    });
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const handleClose = () => {
    // Clear any existing auto-close timeout
    if (autoCloseTimeoutRef.current) {
      clearTimeout(autoCloseTimeoutRef.current);
      autoCloseTimeoutRef.current = null;
    }

    setOpen(false);
    setError(false);
    setSuccess(false);
    setWarning(false);
    setReturnMessage("");
    resetForm();
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const parseExclusionString = (exclusionString) => {
    if (!exclusionString || exclusionString === "00:00:00-00:00:00") {
      return {
        start: dayjs("2000-01-01T00:00:00"),
        end: dayjs("2000-01-01T00:00:00"),
      };
    }

    const parts = exclusionString.split("-");
    if (parts.length === 2) {
      const [startTime, endTime] = parts;
      return {
        start: dayjs(`2000-01-01T${startTime}`),
        end: dayjs(`2000-01-01T${endTime}`),
      };
    }

    return {
      start: dayjs("2000-01-01T00:00:00"),
      end: dayjs("2000-01-01T00:00:00"),
    };
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const formatExclusionString = (startTime, endTime) => {
    const start = startTime ? startTime.format("HH:mm:ss") : "00:00:00";
    const end = endTime ? endTime.format("HH:mm:ss") : "00:00:00";
    return `${start}-${end}`;
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const formik = useFormik({
    initialValues: {
      nSignalInUse: initialData?.nSignalInUse ?? IndicatorDefaults.nSignalInUse,
      nSignalUsedFor: initialData?.nSignalUsedFor ?? IndicatorDefaults.nSignalUsedFor,
      nSignalVisualization: initialData?.nSignalVisualization ?? IndicatorDefaults.nSignalVisualization,
      nDrawInChart: initialData?.nDrawInChart ?? IndicatorDefaults.nDrawInChart,
      nLogDrawingSlot: initialData?.nLogDrawingSlot ?? IndicatorDefaults.nLogDrawingSlot,
      nDrawingSlot: initialData?.nDrawingSlot ?? getNextAvailableDrawingSlot(indicators),
      nSelection: initialData?.nSelection ?? IndicatorDefaults.nSelection,
      strSymbol: initialData?.strSymbol ?? IndicatorDefaults.strSymbol,
      nTimeframe: initialData?.nTimeframe ?? IndicatorDefaults.nTimeframe,
      nSeries: initialData?.nSeries ?? IndicatorDefaults.nSeries,
      nShift: initialData?.nShift ?? IndicatorDefaults.nShift,
      nLineIndex: initialData?.nLineIndex ?? IndicatorDefaults.nLineIndex,
      // All 32 numeric parameters
      dParameter1: initialData?.dParameter1 ?? IndicatorDefaults.dParameter1,
      dParameter2: initialData?.dParameter2 ?? IndicatorDefaults.dParameter2,
      dParameter3: initialData?.dParameter3 ?? IndicatorDefaults.dParameter3,
      dParameter4: initialData?.dParameter4 ?? IndicatorDefaults.dParameter4,
      dParameter5: initialData?.dParameter5 ?? IndicatorDefaults.dParameter5,
      dParameter6: initialData?.dParameter6 ?? IndicatorDefaults.dParameter6,
      dParameter7: initialData?.dParameter7 ?? IndicatorDefaults.dParameter7,
      dParameter8: initialData?.dParameter8 ?? IndicatorDefaults.dParameter8,
      dParameter9: initialData?.dParameter9 ?? IndicatorDefaults.dParameter9,
      dParameter10: initialData?.dParameter10 ?? IndicatorDefaults.dParameter10,
      dParameter11: initialData?.dParameter11 ?? IndicatorDefaults.dParameter11,
      dParameter12: initialData?.dParameter12 ?? IndicatorDefaults.dParameter12,
      dParameter13: initialData?.dParameter13 ?? IndicatorDefaults.dParameter13,
      dParameter14: initialData?.dParameter14 ?? IndicatorDefaults.dParameter14,
      dParameter15: initialData?.dParameter15 ?? IndicatorDefaults.dParameter15,
      dParameter16: initialData?.dParameter16 ?? IndicatorDefaults.dParameter16,
      dParameter17: initialData?.dParameter17 ?? IndicatorDefaults.dParameter17,
      dParameter18: initialData?.dParameter18 ?? IndicatorDefaults.dParameter18,
      dParameter19: initialData?.dParameter19 ?? IndicatorDefaults.dParameter19,
      dParameter20: initialData?.dParameter20 ?? IndicatorDefaults.dParameter20,
      dParameter21: initialData?.dParameter21 ?? IndicatorDefaults.dParameter21,
      dParameter22: initialData?.dParameter22 ?? IndicatorDefaults.dParameter22,
      dParameter23: initialData?.dParameter23 ?? IndicatorDefaults.dParameter23,
      dParameter24: initialData?.dParameter24 ?? IndicatorDefaults.dParameter24,
      dParameter25: initialData?.dParameter25 ?? IndicatorDefaults.dParameter25,
      dParameter26: initialData?.dParameter26 ?? IndicatorDefaults.dParameter26,
      dParameter27: initialData?.dParameter27 ?? IndicatorDefaults.dParameter27,
      dParameter28: initialData?.dParameter28 ?? IndicatorDefaults.dParameter28,
      dParameter29: initialData?.dParameter29 ?? IndicatorDefaults.dParameter29,
      dParameter30: initialData?.dParameter30 ?? IndicatorDefaults.dParameter30,
      dParameter31: initialData?.dParameter31 ?? IndicatorDefaults.dParameter31,
      dParameter32: initialData?.dParameter32 ?? IndicatorDefaults.dParameter32,
      // String parameters
      strParameter1: initialData?.strParameter1 ?? IndicatorDefaults.strParameter1,
      strParameter2: initialData?.strParameter2 ?? IndicatorDefaults.strParameter2,
      strParameter3: initialData?.strParameter3 ?? IndicatorDefaults.strParameter3,
      strParameter4: initialData?.strParameter4 ?? IndicatorDefaults.strParameter4,
      strMt4CustomPath: initialData?.strMt4CustomPath ?? IndicatorDefaults.strMt4CustomPath,
      strMt5CustomPath: initialData?.strMt5CustomPath ?? IndicatorDefaults.strMt5CustomPath,
      // Angle condition
      nAngleCondition: initialData?.nAngleCondition ?? IndicatorDefaults.nAngleCondition,
      dAngleLimit1: initialData?.dAngleLimit1 ?? IndicatorDefaults.dAngleLimit1,
      dAngleLimit2: initialData?.dAngleLimit2 ?? IndicatorDefaults.dAngleLimit2,
      // Value condition
      nValueCondition: initialData?.nValueCondition ?? IndicatorDefaults.nValueCondition,
      dValueLimit1: initialData?.dValueLimit1 ?? IndicatorDefaults.dValueLimit1,
      dValueLimit2: initialData?.dValueLimit2 ?? IndicatorDefaults.dValueLimit2,
      // Other fields
      dForwardAdjustment: initialData?.dForwardAdjustment ?? IndicatorDefaults.dForwardAdjustment,
      nValueAnalysisFeedbackState: initialData?.nValueAnalysisFeedbackState ?? IndicatorDefaults.nValueAnalysisFeedbackState,
      nSignalAnalysisFeedbackState: initialData?.nSignalAnalysisFeedbackState ?? IndicatorDefaults.nSignalAnalysisFeedbackState,
      // Time exclusion - using single string field
      strExclusion: initialData?.strExclusion ?? IndicatorDefaults.strExclusion,
    },
    enableReinitialize: true,
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: (values, { setSubmitting }) => {
      setTimeout(() => {
        try {
          onSave(values);
          setSuccess(true);
          setReturnMessage("Configuration saved successfully!");
          // autoCloseTimeoutRef.current = setTimeout(() => {
          //  handleClose();
          // }, 1500);
        } catch (err) {
          setError(true);
          setReturnMessage("Failed to save configuration.");
          console.error("Save error:", err);
        }
        setSubmitting(false);
      }, 100);
    },
  });

  const { values, errors, touched, isSubmitting, handleSubmit, getFieldProps, setFieldValue, setFieldTouched, setErrors, setTouched, resetForm } =
    formik;

  // ----------------------------------------------------------------------
  // Cross-validation logic
  // ----------------------------------------------------------------------
  useEffect(() => {
    const dValueLimit1 = parseFloat(values.dValueLimit1);
    const dValueLimit2 = parseFloat(values.dValueLimit2);
    const dAngleLimit1 = parseFloat(values.dAngleLimit1);
    const dAngleLimit2 = parseFloat(values.dAngleLimit2);

    // Check Value limit 1 vs 2 (only when both limits are required)
    if (conditionRequiresLimit1(values.nValueCondition) && conditionRequiresLimit2(values.nValueCondition)) {
      if (!Number.isNaN(dValueLimit1) && !Number.isNaN(dValueLimit2)) {
        if (dValueLimit1 >= dValueLimit2) {
          setError(true);
          setReturnMessage(
            <>
              <b>Value limit 1</b> cannot be greater or equal to <b>Value limit 2</b>!
            </>
          );
          return;
        }
      }
    }

    // Check Gradient limit 1 vs 2 (only when both limits are required)
    if (conditionRequiresLimit1(values.nAngleCondition) && conditionRequiresLimit2(values.nAngleCondition)) {
      if (!Number.isNaN(dAngleLimit1) && !Number.isNaN(dAngleLimit2)) {
        if (dAngleLimit1 >= dAngleLimit2) {
          setError(true);
          setReturnMessage(
            <>
              <b>Gradient limit 1</b> cannot be greater or equal to <b>Gradient limit 2</b>!
            </>
          );
          return;
        }
      }
    }

    // If all validations pass, clear error state
    setError(false);
    setReturnMessage("");
  }, [values.nValueCondition, values.dValueLimit1, values.dValueLimit2, values.nAngleCondition, values.dAngleLimit1, values.dAngleLimit2]);

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const exclusionTimes = parseExclusionString(values.strExclusion);

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const handleClickOpen = (scrollType) => () => {
    setOpen(true);
    setScroll(scrollType);
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const handleIndicatorSelectionChange = (event) => {
    const selectedId = event.target.value;

    if (!selectedId) {
      // Clear selection
      setSelectedConfig(null);
      setFieldValue("nSelection", null);
      return;
    }

    const config = ALL_CONFIGS[selectedId];

    if (config) {
      setSelectedConfig(config);

      // Clear all errors and touched state when changing indicator
      setErrors({});
      setTouched({});

      // Update form values with defaults
      setFieldValue("nSelection", selectedId);
      setFieldValue("nTimeframe", config.defaultValues.nTimeframe);
      setFieldValue("nSeries", config.defaultValues.nSeries);
      setFieldValue("nLineIndex", "0"); // Reset to first line option

      // RESET ALL PARAMETERS TO 0 FIRST - This is the key fix
      for (let i = 1; i <= 32; i += 1) {
        setFieldValue(`dParameter${i}`, "0");
      }

      // RESET ALL STRING PARAMETERS TO EMPTY - This is also important
      for (let i = 1; i <= 4; i += 1) {
        setFieldValue(`strParameter${i}`, "");
      }
      setFieldValue("strMt4CustomPath", "");
      setFieldValue("strMt5CustomPath", "");

      // THEN set the specific parameter defaults for the selected indicator
      config.parameters.forEach((param) => {
        setFieldValue(param.key, config.defaultValues[param.key]);
      });

      // Set string parameter defaults for the selected indicator
      config.stringParameters.forEach((param) => {
        setFieldValue(param.key, config.defaultValues[param.key]);
      });
    }
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  useEffect(() => {
    if (values.nSelection && ALL_CONFIGS[values.nSelection]) {
      setSelectedConfig(ALL_CONFIGS[values.nSelection]);
    }
  }, [values.nSelection]);

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const handleLineIndexChange = (event) => {
    setFieldValue("nLineIndex", event.target.value.toString());
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  let saveAndCloseDisabled = false;

  const hasValidationErrors = () => Object.keys(formik.errors).length > 0;
  if (hasValidationErrors()) {
    saveAndCloseDisabled = true;
  }

  // Disable save button if cross-validation fails
  if (error) {
    saveAndCloseDisabled = true;
  }

  // ----------------------------------------------------------------------
  // Main component
  // ----------------------------------------------------------------------
  return (
    <>
      <B1Tiny fullWidth disableRipple={Boolean(true)} onClick={handleClickOpen("paper")}>
        Configure indicator
      </B1Tiny>

      <ModalDialog
        open={open}
        handleClose={handleClose}
        scroll={scroll}
        outlet={
          <>
            <DialogTitle
              id="dialog-title"
              sx={{
                background: "#F2F2F2",
                textTransform: "none",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                alignContent: "center",
                py: 1,
                pl: 2,
                pr: 1,
              }}
            >
              <Typography variant="contentBig">Configure indicator</Typography>

              <IconButton onClick={handleClose} sx={{ borderRadius: "10px" }}>
                <Iconify icon="eva:close-outline" minWidth={25} minHeight={25} color="#2E2E2E" />
              </IconButton>
            </DialogTitle>

            <FormikProvider value={formik}>
              <DialogContent
                dividers
                sx={{
                  background: "#F2F2F2",
                  borderTop: "1px solid #C8C8C8",
                  borderBottom: "0px solid #C8C8C8",
                  px: 2,
                  pt: 2,
                  pb: 1,
                  width: "100%",
                  boxSizing: "border-box",
                  overflowY: "scroll",
                  overflowX: "hidden",
                  // Custom scrollbar styling
                  "&::-webkit-scrollbar": {
                    width: "16px",
                  },
                  "&::-webkit-scrollbar-track": {
                    backgroundColor: "#e0e0e0",
                    borderRadius: "6px",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "#888888",
                    borderRadius: "6px",
                    border: "2px solid #e0e0e0",
                  },
                  "&::-webkit-scrollbar-thumb:hover": {
                    backgroundColor: "#666666",
                  },
                  // Firefox scrollbar styling
                  scrollbarWidth: "thin",
                  scrollbarColor: "#888888 #e0e0e0",
                }}
              >
                <Stack spacing={2}>
                  {/* ---------------------------------------------------------------------- */}
                  {/* Grid */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 2,
                    }}
                  >
                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* nSignalInUse */}
                    {/* ---------------------------------------------------------------------- */}
                    <Box>
                      <SmartParameter
                        value="Signal in use"
                        helpReference="HandleIndicators"
                        helpText=""
                        helpLink=""
                        fieldName="nSignalInUse"
                        touched={touched}
                        errors={errors}
                      />
                      <ModalContentSwitch
                        sx={{ mt: "12px" }}
                        checked={values.nSignalInUse === true}
                        onChange={(event) => {
                          setFieldValue("nSignalInUse", event.target.checked);
                        }}
                      />
                    </Box>

                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* nSignalUsedFor */}
                    {/* ---------------------------------------------------------------------- */}
                    <Box>
                      <SmartParameter
                        value="Signal used for"
                        helpReference="HandleIndicators"
                        helpText=""
                        helpLink=""
                        fieldName="nSignalUsedFor"
                        touched={touched}
                        errors={errors}
                      />
                      {initialData === null || !initialData.forwardUsed ? (
                        <FormControl fullWidth>
                          <Select
                            {...getFieldProps("nSignalUsedFor")}
                            input={<ModalContentOutlinedInput />}
                            error={Boolean(touched.nSignalUsedFor && errors.nSignalUsedFor)}
                          >
                            <ModalContentMenuItem value="0">Entry</ModalContentMenuItem>
                            <ModalContentMenuItem value="1">Exit</ModalContentMenuItem>
                            <ModalContentMenuItem value="2">Forward</ModalContentMenuItem>
                          </Select>
                        </FormControl>
                      ) : (
                        <Typography variant="contentSmall" sx={{ fontWeight: "bold" }}>
                          This indicator is linked to another component that relies on its value, so it cannot be modified. To make changes, please
                          release the signal in the associated component first.
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Divider */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box>
                    <Divider sx={{ mt: 2, mb: 2, borderColor: "#C8C8C8" }} />
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Parameter configuration */}
                  {/* nSelection */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box>
                    <SmartParameter
                      value="Selected indicator"
                      helpReference="HandleIndicators"
                      helpText=""
                      helpLink=""
                      fieldName="Selected indicator"
                      touched={touched}
                      errors={errors}
                    />
                    <FormControl fullWidth>
                      <Select
                        value={values.nSelection}
                        onChange={handleIndicatorSelectionChange}
                        input={<ModalContentOutlinedInput />}
                        error={Boolean(touched.nSelection && errors.nSelection)}
                        sx={{ mb: 2 }}
                      >
                        {INDICATOR_OPTIONS.map((option) => (
                          <ModalContentMenuItem key={option.value} value={option.value}>
                            <Box>
                              <Typography variant="contentSmall" sx={{ fontWeight: "bold" }}>
                                {option.label}{" "}
                              </Typography>
                            </Box>
                          </ModalContentMenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    {selectedConfig && (selectedConfig.description || selectedConfig.image1) && (
                      <Box>
                        <B3Tiny
                          fullWidth
                          variant="outlined"
                          onClick={() => {
                            setCollapseIndicator(!collapseIndicator);
                          }}
                        >
                          {collapseIndicator ? "Hide help section" : "Show help section"}
                        </B3Tiny>
                      </Box>
                    )}

                    {selectedConfig && (selectedConfig.description || selectedConfig.image1) && (
                      <Collapse sx={{ p: "3px" }} in={collapseIndicator} timeout="auto" unmountOnExit>
                        <Box>
                          <Box sx={{ py: 1 }}>
                            <Typography variant="contentMiddle" sx={{ mt: 0.5 }}>
                              {selectedConfig.description}
                              <br />
                            </Typography>
                          </Box>

                          {selectedConfig.image1 && (
                            <Box
                              sx={{
                                borderRadius: "10px",
                                py: 2,
                                boxShadow: "0px 0px 3px 0px rgba(0,0,0,0.5)",
                                backgroundColor: "#DCDCDC",
                              }}
                            >
                              <CardMedia
                                component="img"
                                height="auto"
                                image={selectedConfig.image1}
                                alt={selectedConfig.name}
                                sx={{
                                  objectFit: "contain",
                                  maxHeight: selectedConfig.image1Height,
                                }}
                              />
                            </Box>
                          )}

                          {selectedConfig.image2 && (
                            <Box
                              sx={{
                                borderRadius: "10px",
                                mt: 2,
                                py: 2,
                                boxShadow: "0px 0px 3px 0px rgba(0,0,0,0.5)",
                                backgroundColor: "#DCDCDC",
                              }}
                            >
                              <CardMedia
                                component="img"
                                height="auto"
                                image={selectedConfig.image2}
                                alt={selectedConfig.name}
                                sx={{
                                  objectFit: "contain",
                                  maxHeight: selectedConfig.image2Height,
                                }}
                              />
                            </Box>
                          )}
                        </Box>
                      </Collapse>
                    )}
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Parameter configuration */}
                  {/* nLineIndex */}
                  {/* ---------------------------------------------------------------------- */}
                  {selectedConfig && (
                    <>
                      {selectedConfig.id === "1016" || selectedConfig.id === "2026" ? (
                        <Box>
                          <SmartParameter
                            value="Used line"
                            helpReference="HandleIndicators"
                            helpText=""
                            helpLink=""
                            fieldName="nLineIndex"
                            touched={touched}
                            errors={errors}
                          />
                          <SmartNumberInput
                            textFieldName="nLineIndex"
                            defaultLimits={{
                              ...DEFAULT_LIMITS,
                              ...(selectedConfig.limits || {}),
                            }}
                            formik={formik}
                          />
                        </Box>
                      ) : (
                        /* For regular indicators, show radio buttons only if there are meaningful line options */
                        selectedConfig.lineIndices &&
                        selectedConfig.lineIndices.length > 1 &&
                        !(selectedConfig.lineIndices.length === 1 && selectedConfig.lineIndices[0].label === "Default") && (
                          <>
                            <SmartParameter
                              value="Used line"
                              helpReference="HandleIndicators"
                              helpText=""
                              helpLink=""
                              fieldName="nLineIndex"
                              touched={touched}
                              errors={errors}
                            />
                            <Box>
                              <FormControl component="fieldset">
                                <ModalContentRadioGroup value={values.nLineIndex.toString()} onChange={handleLineIndexChange} row>
                                  {selectedConfig.lineIndices.map((line) => (
                                    <FormControlLabel
                                      key={line.value}
                                      value={line.value}
                                      control={<Radio size="small" />}
                                      label={line.label}
                                      sx={{ mr: 2 }}
                                    />
                                  ))}
                                </ModalContentRadioGroup>
                              </FormControl>
                            </Box>
                          </>
                        )
                      )}
                    </>
                  )}

                  {/* ---------------------------------------------------------------------- */}
                  {/* Parameter configuration */}
                  {/* ---------------------------------------------------------------------- */}
                  {selectedConfig && selectedConfig.parameters && selectedConfig.parameters.length > 0 && (
                    <Box>
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: (() => {
                            const paramCount = selectedConfig.parameters.length;
                            if (paramCount === 1) return "1fr";
                            if (paramCount === 2) return "repeat(2, 1fr)";
                            return "repeat(3, 1fr)";
                          })(),
                          gap: 2,
                        }}
                      >
                        {selectedConfig.parameters.map((param) => {
                          // Create combined limits - indicator-specific limits override DEFAULT_LIMITS
                          const combinedLimits = {
                            ...DEFAULT_LIMITS,
                            ...(selectedConfig.limits || {}),
                          };

                          return (
                            <Box key={param.key}>
                              <SmartParameter
                                value={param.label}
                                helpReference="HandleIndicators"
                                helpText={param.description}
                                helpLink=""
                                fieldName={param.key}
                                touched={touched}
                                errors={errors}
                              />
                              <SmartNumberInput textFieldName={param.key} defaultLimits={combinedLimits} formik={formik} />
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>
                  )}

                  {/* ---------------------------------------------------------------------- */}
                  {/* Parameter configuration */}
                  {/* ---------------------------------------------------------------------- */}
                  {selectedConfig && selectedConfig.stringParameters && selectedConfig.stringParameters.length > 0 && (
                    <>
                      {selectedConfig.stringParameters.map((param) => (
                        <FastField key={param.key} name={param.key}>
                          {({ field, meta }) => (
                            <Box>
                              <SmartParameter
                                value={param.label}
                                helpReference="HandleIndicators"
                                helpText={param.description}
                                helpLink=""
                                fieldName={field.name}
                                touched={touched}
                                errors={errors}
                              />
                              <SmartTextInput textFieldName={field.name} defaultLimits={DEFAULT_LIMITS} formik={formik} />
                            </Box>
                          )}
                        </FastField>
                      ))}
                    </>
                  )}

                  {/* ---------------------------------------------------------------------- */}
                  {/* Grid */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 2,
                    }}
                  >
                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* strSymbol */}
                    {/* ---------------------------------------------------------------------- */}
                    <Box>
                      <SmartParameter
                        value="Symbol"
                        helpReference="HandleIndicators"
                        helpText=""
                        helpLink=""
                        fieldName="strSymbol"
                        touched={touched}
                        errors={errors}
                      />
                      <SmartTextInput textFieldName="strSymbol" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                    </Box>

                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* nTimeframe */}
                    {/* ---------------------------------------------------------------------- */}
                    <Box>
                      <SmartParameter
                        value="Timeframe"
                        helpReference="HandleIndicators"
                        helpText=""
                        helpLink=""
                        fieldName="nTimeframe"
                        touched={touched}
                        errors={errors}
                      />
                      <FormControl fullWidth>
                        <Select
                          {...getFieldProps("nTimeframe")}
                          input={<ModalContentOutlinedInput />}
                          error={Boolean(touched.nTimeframe && errors.nTimeframe)}
                        >
                          {TIMEFRAME_OPTIONS.map((option) => (
                            <ModalContentMenuItem key={option.value} value={option.value}>
                              {option.label}
                            </ModalContentMenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Grid */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 2,
                    }}
                  >
                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* nSeries */}
                    {/* ---------------------------------------------------------------------- */}
                    <Box>
                      <SmartParameter
                        value="Series"
                        helpReference="HandleIndicators"
                        helpText=""
                        helpLink=""
                        fieldName="nSeries"
                        touched={touched}
                        errors={errors}
                      />
                      <FormControl fullWidth>
                        <Select
                          {...getFieldProps("nSeries")}
                          input={<ModalContentOutlinedInput />}
                          error={Boolean(touched.nSeries && errors.nSeries)}
                        >
                          {SERIES_OPTIONS.map((option) => (
                            <ModalContentMenuItem key={option.value} value={option.value}>
                              {option.label}
                            </ModalContentMenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>

                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* nShift */}
                    {/* ---------------------------------------------------------------------- */}
                    <Box>
                      <SmartParameter
                        value="Shift"
                        helpReference="HandleIndicators"
                        helpText=""
                        helpLink=""
                        fieldName="nShift"
                        touched={touched}
                        errors={errors}
                      />
                      <SmartNumberInput textFieldName="nShift" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                    </Box>
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Divider */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box>
                    <Divider sx={{ mt: 2, mb: 2, borderColor: "#C8C8C8" }} />
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Parameter configuration */}
                  {/* nValueCondition */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box>
                    <SmartParameter
                      value="Value condition"
                      helpReference="HandleIndicators"
                      helpText=""
                      helpLink=""
                      fieldName="nValueCondition"
                      touched={touched}
                      errors={errors}
                    />
                    <FormControl fullWidth>
                      <Select
                        {...getFieldProps("nValueCondition")}
                        input={<ModalContentOutlinedInput />}
                        onChange={(event) => {
                          const newValue = event.target.value;
                          const currentValue = values.nValueCondition;
                          if (newValue !== currentValue) {
                            setFieldValue("nValueCondition", newValue);
                          }
                        }}
                      >
                        <ModalContentMenuItem value="0">Off</ModalContentMenuItem>
                        <ModalContentMenuItem value="1">Smaller limit 1</ModalContentMenuItem>
                        <ModalContentMenuItem value="2">Greater limit 1</ModalContentMenuItem>
                        <ModalContentMenuItem value="3">Equals limit 1</ModalContentMenuItem>
                        <ModalContentMenuItem value="4">Between limit 1 and limit 2</ModalContentMenuItem>
                        <ModalContentMenuItem value="5">Outside limit 1 and limit 2</ModalContentMenuItem>
                        <ModalContentMenuItem value="6">Cross limit 1</ModalContentMenuItem>
                        <ModalContentMenuItem value="7">Limit 1 crossing</ModalContentMenuItem>
                        <ModalContentMenuItem value="8">Cross any</ModalContentMenuItem>
                      </Select>
                    </FormControl>
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Help Image for Value Condition */}
                  {/* ---------------------------------------------------------------------- */}
                  {VALUE_CONDITION_HELP_SECTIONS[values.nValueCondition] && (
                    <Box>
                      <B3Tiny
                        fullWidth
                        variant="outlined"
                        onClick={() => {
                          setCollapseValue(!collapseValue);
                        }}
                      >
                        {collapseValue ? "Hide help section" : "Show help section"}
                      </B3Tiny>

                      <Collapse sx={{ p: "3px" }} in={collapseValue} timeout="auto" unmountOnExit>
                        <Box sx={{ py: 1 }}>
                          <Typography variant="contentMiddle" sx={{ mt: 0.5 }}>
                            {VALUE_CONDITION_HELP_SECTIONS[values.nValueCondition].description}
                            <br />
                          </Typography>
                        </Box>

                        <Box
                          sx={{
                            borderRadius: "10px",
                            py: 2,
                            boxShadow: "0px 0px 3px 0px rgba(0,0,0,0.5)",
                            backgroundColor: "white",
                          }}
                        >
                          <CardMedia
                            component="img"
                            image={VALUE_CONDITION_HELP_SECTIONS[values.nValueCondition].image}
                            alt="Value condition help image"
                            sx={{
                              objectFit: "contain",
                              maxHeight: "163px",
                            }}
                          />
                        </Box>
                      </Collapse>
                    </Box>
                  )}

                  {/* ---------------------------------------------------------------------- */}
                  {/* Grid */}
                  {/* ---------------------------------------------------------------------- */}
                  {conditionRequiresLimit1(values.nValueCondition) && (
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: conditionRequiresLimit2(values.nValueCondition) ? "1fr 1fr" : "1fr",
                        gap: 2,
                      }}
                    >
                      {/* ---------------------------------------------------------------------- */}
                      {/* Parameter configuration */}
                      {/* dValueLimit1 */}
                      {/* ---------------------------------------------------------------------- */}
                      <Box>
                        <SmartParameter
                          value="Value limit 1"
                          helpReference="HandleIndicators"
                          helpText=""
                          helpLink=""
                          fieldName="dValueLimit1"
                          touched={touched}
                          errors={errors}
                        />
                        <SmartNumberInput textFieldName="dValueLimit1" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                      </Box>
                      {/* ---------------------------------------------------------------------- */}
                      {/* Parameter configuration */}
                      {/* dValueLimit2 */}
                      {/* ---------------------------------------------------------------------- */}
                      {conditionRequiresLimit2(values.nValueCondition) && (
                        <Box>
                          <SmartParameter
                            value="Value limit 2"
                            helpReference="HandleIndicators"
                            helpText=""
                            helpLink=""
                            fieldName="dValueLimit2"
                            touched={touched}
                            errors={errors}
                          />
                          <SmartNumberInput textFieldName="dValueLimit2" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                        </Box>
                      )}
                    </Box>
                  )}

                  {/* ---------------------------------------------------------------------- */}
                  {/* Divider */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box>
                    <Divider sx={{ mt: 2, mb: 2, borderColor: "#C8C8C8" }} />
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Parameter configuration */}
                  {/* nAngleCondition */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box>
                    <SmartParameter
                      value="Gradient condition"
                      helpReference="HandleIndicators"
                      helpText=""
                      helpLink=""
                      fieldName="nAngleCondition"
                      touched={touched}
                      errors={errors}
                    />
                    <FormControl fullWidth>
                      <Select
                        {...getFieldProps("nAngleCondition")}
                        input={<ModalContentOutlinedInput />}
                        onChange={(event) => {
                          const newValue = event.target.value;
                          const currentValue = values.nAngleCondition;
                          if (newValue !== currentValue) {
                            setFieldValue("nAngleCondition", newValue);
                          }
                        }}
                      >
                        <ModalContentMenuItem value="0">Off</ModalContentMenuItem>
                        <ModalContentMenuItem value="1">Smaller limit 1</ModalContentMenuItem>
                        <ModalContentMenuItem value="2">Greater limit 1</ModalContentMenuItem>
                        <ModalContentMenuItem value="3">Equals limit 1</ModalContentMenuItem>
                        <ModalContentMenuItem value="4">Between limit 1 and limit 2</ModalContentMenuItem>
                        <ModalContentMenuItem value="5">Outside limit 1 and limit 2</ModalContentMenuItem>
                        <ModalContentMenuItem value="6">Cross limit 1</ModalContentMenuItem>
                        <ModalContentMenuItem value="7">Limit 1 crossing</ModalContentMenuItem>
                        <ModalContentMenuItem value="8">Cross any</ModalContentMenuItem>
                      </Select>
                    </FormControl>
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Help Image for Gradient Condition */}
                  {/* ---------------------------------------------------------------------- */}
                  {GRADIENT_CONDITION_HELP_SECTIONS[values.nAngleCondition] && (
                    <Box>
                      <B3Tiny
                        fullWidth
                        variant="outlined"
                        onClick={() => {
                          setCollapseGradient(!collapseGradient);
                        }}
                      >
                        {collapseGradient ? "Hide help section" : "Show help section"}
                      </B3Tiny>

                      <Collapse sx={{ p: "3px" }} in={collapseGradient} timeout="auto" unmountOnExit>
                        <Box sx={{ py: 1 }}>
                          <Typography variant="contentMiddle" sx={{ mt: 0.5 }}>
                            {GRADIENT_CONDITION_HELP_SECTIONS[values.nValueCondition].description}
                            <br />
                          </Typography>
                        </Box>

                        <Box
                          sx={{
                            borderRadius: "10px",
                            py: 2,
                            boxShadow: "0px 0px 3px 0px rgba(0,0,0,0.5)",
                            backgroundColor: "white",
                          }}
                        >
                          <CardMedia
                            component="img"
                            image={GRADIENT_CONDITION_HELP_SECTIONS[values.nAngleCondition].image}
                            alt="Gradient condition help image"
                            sx={{
                              objectFit: "contain",
                              maxHeight: "163px",
                            }}
                          />
                        </Box>
                      </Collapse>
                    </Box>
                  )}

                  {/* ---------------------------------------------------------------------- */}
                  {/* Grid */}
                  {/* ---------------------------------------------------------------------- */}
                  {conditionRequiresLimit1(values.nAngleCondition) && (
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: conditionRequiresLimit2(values.nAngleCondition) ? "1fr 1fr" : "1fr",
                        gap: 2,
                      }}
                    >
                      {/* ---------------------------------------------------------------------- */}
                      {/* Parameter configuration */}
                      {/* dAngleLimit1 */}
                      {/* ---------------------------------------------------------------------- */}
                      <Box>
                        <SmartParameter
                          value="Gradient limit 1"
                          helpReference="HandleIndicators"
                          helpText=""
                          helpLink=""
                          fieldName="dAngleLimit1"
                          touched={touched}
                          errors={errors}
                        />
                        <SmartNumberInput textFieldName="dAngleLimit1" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                      </Box>
                      {/* ---------------------------------------------------------------------- */}
                      {/* Parameter configuration */}
                      {/* dAngleLimit2 */}
                      {/* ---------------------------------------------------------------------- */}
                      {conditionRequiresLimit2(values.nAngleCondition) && (
                        <Box>
                          <SmartParameter
                            value="Gradient limit 2"
                            helpReference="HandleIndicators"
                            helpText=""
                            helpLink=""
                            fieldName="dAngleLimit2"
                            touched={touched}
                            errors={errors}
                          />
                          <SmartNumberInput textFieldName="dAngleLimit2" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                        </Box>
                      )}
                    </Box>
                  )}

                  {/* ---------------------------------------------------------------------- */}
                  {/* Divider */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box>
                    <Divider sx={{ mt: 2, mb: 2, borderColor: "#C8C8C8" }} />
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Grid */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: (() => {
                        if (professionalMode !== true) return "1fr"; // Only Signal visualization
                        return "1fr 1fr 1fr 1fr";
                      })(),
                      gap: 2,
                    }}
                  >
                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* nSignalVisualization */}
                    {/* ---------------------------------------------------------------------- */}
                    <Box>
                      <SmartParameter
                        value="Signal visualization"
                        helpReference="HandleIndicators"
                        helpText=""
                        helpLink=""
                        fieldName="nSignalVisualization"
                        touched={touched}
                        errors={errors}
                      />
                      <ModalContentSwitch
                        sx={{ mt: "12px" }}
                        checked={values.nSignalVisualization === true}
                        onChange={(event) => {
                          setFieldValue("nSignalVisualization", event.target.checked);
                        }}
                      />
                    </Box>

                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* nDrawInChart */}
                    {/* ---------------------------------------------------------------------- */}
                    {professionalMode === true && (
                      <Box>
                        <SmartParameter
                          value="Draw in chart"
                          helpReference="HandleIndicators"
                          helpText=""
                          helpLink=""
                          fieldName="nDrawInChart"
                          touched={touched}
                          errors={errors}
                        />
                        <ModalContentSwitch
                          sx={{ mt: "12px", mb: "12px" }}
                          checked={values.nDrawInChart === true}
                          onChange={(event) => {
                            setFieldValue("nDrawInChart", event.target.checked);
                          }}
                        />
                      </Box>
                    )}

                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* nLogDrawingSlot */}
                    {/* ---------------------------------------------------------------------- */}
                    {professionalMode === true && values.nDrawInChart === true && (
                      <>
                        <Box>
                          <SmartParameter
                            value="Log drawing slot"
                            helpReference="HandleIndicators"
                            helpText=""
                            helpLink=""
                            fieldName="nLogDrawingSlot"
                            touched={touched}
                            errors={errors}
                          />
                          <ModalContentSwitch
                            sx={{ mt: "12px" }}
                            checked={values.nLogDrawingSlot === true}
                            onChange={(event) => {
                              setFieldValue("nLogDrawingSlot", event.target.checked);
                            }}
                          />
                        </Box>

                        <Box>
                          <SmartParameter
                            value="Drawing slot"
                            helpReference="HandleIndicators"
                            helpText=""
                            helpLink=""
                            fieldName="nDrawingSlot"
                            touched={touched}
                            errors={errors}
                          />
                          <SmartNumberInput textFieldName="nDrawingSlot" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                        </Box>
                      </>
                    )}
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Divider */}
                  {/* ---------------------------------------------------------------------- */}
                  {professionalMode === true ? (
                    <Box>
                      <Divider sx={{ mt: 2, mb: 2, borderColor: "#C8C8C8" }} />
                    </Box>
                  ) : null}

                  {/* ---------------------------------------------------------------------- */}
                  {/* Parameter configuration */}
                  {/* strExclusion */}
                  {/* ---------------------------------------------------------------------- */}
                  {professionalMode === true ? (
                    <Box>
                      <SmartParameter
                        value="Exclusion time range"
                        helpReference="HandleIndicators"
                        helpText=""
                        helpLink=""
                        fieldName="strExclusion"
                        touched={touched}
                        errors={errors}
                      />
                      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={enCA}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <ModalTimePicker
                            value={exclusionTimes.start}
                            onChange={(value) => {
                              const newExclusionString = formatExclusionString(value, exclusionTimes.end);
                              setFieldValue("strExclusion", newExclusionString);
                            }}
                            ampm={false}
                            views={["hours", "minutes", "seconds"]}
                            slotProps={ModalTimePickerSlotProps}
                          />
                          <Typography variant="contentTiny">-</Typography>
                          <ModalTimePicker
                            value={exclusionTimes.end}
                            onChange={(value) => {
                              const newExclusionString = formatExclusionString(exclusionTimes.start, value);
                              setFieldValue("strExclusion", newExclusionString);
                            }}
                            ampm={false}
                            views={["hours", "minutes", "seconds"]}
                            slotProps={ModalTimePickerSlotProps}
                          />
                        </Box>
                      </LocalizationProvider>
                    </Box>
                  ) : null}

                  {/* ---------------------------------------------------------------------- */}
                  {/* Grid */}
                  {/* ---------------------------------------------------------------------- */}
                  {professionalMode === true ? (
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr 1fr",
                        gap: 2,
                      }}
                    >
                      {/* ---------------------------------------------------------------------- */}
                      {/* Parameter configuration */}
                      {/* nValueAnalysisFeedbackState */}
                      {/* ---------------------------------------------------------------------- */}
                      <Box>
                        <SmartParameter
                          value="Value analysis feedback state"
                          helpReference="HandleIndicators"
                          helpText=""
                          helpLink=""
                          fieldName="nValueAnalysisFeedbackState"
                          touched={touched}
                          errors={errors}
                        />
                        <ModalContentSwitch
                          sx={{ mt: "12px" }}
                          checked={values.nValueAnalysisFeedbackState === true}
                          onChange={(event) => {
                            setFieldValue("nValueAnalysisFeedbackState", event.target.checked);
                          }}
                        />
                      </Box>

                      {/* ---------------------------------------------------------------------- */}
                      {/* Parameter configuration */}
                      {/* nSignalAnalysisFeedbackState */}
                      {/* ---------------------------------------------------------------------- */}
                      <Box>
                        <SmartParameter
                          value="Signal analysis feedback state"
                          helpReference="HandleIndicators"
                          helpText=""
                          helpLink=""
                          fieldName="nSignalAnalysisFeedbackState"
                          touched={touched}
                          errors={errors}
                        />
                        <ModalContentSwitch
                          sx={{ mt: "12px" }}
                          checked={values.nSignalAnalysisFeedbackState === true}
                          onChange={(event) => {
                            setFieldValue("nSignalAnalysisFeedbackState", event.target.checked);
                          }}
                        />
                      </Box>

                      {/* ---------------------------------------------------------------------- */}
                      {/* Parameter configuration */}
                      {/* dForwardAdjustment */}
                      {/* ---------------------------------------------------------------------- */}
                      <Box>
                        <SmartParameter
                          value="Forward adjustment"
                          helpReference="HandleIndicators"
                          helpText=""
                          helpLink=""
                          fieldName="dForwardAdjustment"
                          touched={touched}
                          errors={errors}
                        />
                        <SmartNumberInput textFieldName="dForwardAdjustment" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                      </Box>
                    </Box>
                  ) : null}
                </Stack>
              </DialogContent>

              <Box sx={{ px: 1, pt: 1, mb: -1, background: "#F2F2F2" }}>
                <RequestMessage wrapperRef={wrapperRef} error={error} warning={warning} success={success} returnMessage={returnMessage} />
              </Box>

              <DialogActions
                sx={{
                  background: "#F2F2F2",
                  p: 2,
                  pt: 0,
                  display: "flex",
                  alignItems: "flex-start",
                  flexDirection: "column",
                  width: "100%",
                  boxSizing: "border-box",
                }}
              >
                <Box sx={{ pt: 1, width: "100%", display: "flex", gap: 1 }}>
                  <ModalActionsLoadingButton
                    fullWidth
                    size="large"
                    type="submit"
                    variant="contained"
                    onClick={handleReset}
                    loading={isSubmitting}
                    sx={{ mt: 1 }}
                  >
                    Reset
                  </ModalActionsLoadingButton>

                  <ModalActionsLoadingButton
                    fullWidth
                    disabled={saveAndCloseDisabled}
                    size="large"
                    type="submit"
                    variant="contained"
                    onClick={handleSubmit}
                    loading={isSubmitting}
                    sx={{ mt: 1 }}
                  >
                    Save
                  </ModalActionsLoadingButton>

                  <ModalActionsLoadingButton
                    fullWidth
                    size="large"
                    type="submit"
                    variant="contained"
                    onClick={handleClose}
                    loading={isSubmitting}
                    sx={{ mt: 1 }}
                  >
                    Close
                  </ModalActionsLoadingButton>
                </Box>
              </DialogActions>
            </FormikProvider>
          </>
        }
      />
    </>
  );
});
